import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Image as ImageIcon, Send, CheckCircle2, AlertCircle, Loader2, ExternalLink, Crop, Wand2, RefreshCw, ChevronLeft, ChevronRight, Link2 } from 'lucide-react';
import MicButton from '../components/MicButton';
import { getCurrentUser, getAccessToken } from '../services/auth';
import { neonQuery, callClaude } from '../services/api';
import MediaPicker from '../components/MediaPicker';
import OrganicBuilder from './OrganicBuilder';
import ViralBoost from './ViralBoost';
import InstagramBuilder from './InstagramBuilder';
import TikTokBuilder from './TikTokBuilder';
import LinkBuilder from './LinkBuilder';
import PartnerLinks from '../components/PartnerLinks';
import MetaCampaigns from './MetaCampaigns';
import UnifiedCalendar from '../components/UnifiedCalendar';
import UnifiedPerformance from '../components/UnifiedPerformance';
import organicFetch from '../lib/organicFetch';

// Meta placement definitions. Each entry knows (a) how to crop for it,
// (b) which Meta publisher_platforms + positions to target for ad sets
// that carry creatives of this ratio.
const PLACEMENTS = [
  {
    key: 'square', label: 'Feed 1:1', ratio: 1 / 1, dims: '1080 × 1080',
    hint: 'Safest all-rounder', rank: 2,
    targeting: {
      publisher_platforms: ['facebook', 'instagram'],
      facebook_positions: ['feed', 'marketplace'],
      instagram_positions: ['stream', 'explore'],
    },
  },
  {
    key: 'portrait', label: 'Feed 4:5', ratio: 4 / 5, dims: '1080 × 1350',
    hint: 'Most screen in feed', rank: 1,
    targeting: {
      publisher_platforms: ['facebook', 'instagram'],
      facebook_positions: ['feed'],
      instagram_positions: ['stream', 'explore'],
    },
  },
  {
    key: 'stories', label: 'Stories 9:16', ratio: 9 / 16, dims: '1080 × 1920',
    hint: 'Stories and Reels only', rank: 3,
    targeting: {
      publisher_platforms: ['facebook', 'instagram'],
      facebook_positions: ['story'],
      instagram_positions: ['story', 'reels'],
    },
  },
  {
    key: 'link', label: 'Link 1.91:1', ratio: 1.91 / 1, dims: '1200 × 628',
    hint: 'Desktop / right column', rank: 4, weak: true,
    targeting: {
      publisher_platforms: ['facebook'],
      facebook_positions: ['feed', 'right_hand_column'],
    },
  },
];
const PLACEMENT_BY_KEY = Object.fromEntries(PLACEMENTS.map((p) => [p.key, p]));

function formatPushError(data, status) {
  const base = data?.error || `HTTP ${status}`;
  const me = data?.metaError;
  if (!me) return base;
  const detail = me.error_user_msg || me.message;
  const subcode = me.error_subcode != null ? ` (subcode ${me.error_subcode})` : '';
  const trace = me.fbtrace_id ? ` · trace ${me.fbtrace_id}` : '';
  return detail ? `${base}\n→ ${detail}${subcode}${trace}` : base;
}

const OBJECTIVES = [
  { value: 'OUTCOME_SALES',       label: 'Sales / Conversions' },
  { value: 'OUTCOME_TRAFFIC',     label: 'Traffic (link clicks)' },
  { value: 'OUTCOME_ENGAGEMENT',  label: 'Engagement' },
  { value: 'OUTCOME_AWARENESS',   label: 'Awareness / Reach' },
  { value: 'OUTCOME_LEADS',       label: 'Leads' },
];

const CTA_TYPES = [
  'SHOP_NOW', 'LEARN_MORE', 'SIGN_UP', 'GET_OFFER', 'ORDER_NOW',
  'BOOK_TRAVEL', 'CONTACT_US', 'SUBSCRIBE', 'DOWNLOAD', 'WATCH_MORE',
];

function slug(s) {
  return (s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

// Ad-order attribution only works if the destination is on crabplace.com — that's
// where the GTM capture tag + cp_attrib cookie live. This tool auto-adds the UTMs,
// so the ONE rule a human still has to get right is "send people to our own site."
// Surface it as a ✓/⚠ so nobody has to remember it.
function destinationIsTracked(url) {
  try {
    const h = new URL(url).hostname.toLowerCase();
    return h === 'crabplace.com' || h.endsWith('.crabplace.com');
  } catch { return false; }
}

// Preset prompts — click to prefill the AI suggestion textarea. Keep this short
// and tactical; the point is to skip retyping the common seasonal briefs, not
// to build an exhaustive catalog.
const PROMPT_PRESETS = [
  {
    label: "Mother's Day",
    text: "Mother's Day push. Get Mom something special this year. Show a woman smiling eating seafood or with seafood on the table. Can be with a man or a group as long as she's the featured subject.",
  },
  {
    label: "Father's Day",
    text: "Father's Day push. Get Dad something he'll actually love. Show a man (solo or with family) enjoying a real Maryland crab feast — steamed crabs, beer, picnic table energy. Authentic UGC preferred.",
  },
  {
    label: "Memorial Day",
    text: "Memorial Day weekend crab feast. Outdoor gathering vibes — backyard, dock, picnic table. Summer kickoff energy. Steamed blue crabs front-and-center, group scenes welcome.",
  },
  {
    label: "4th of July",
    text: "4th of July crab feast push. Summer cookout energy, outdoor tables, families and groups. Hero the steamed crabs + sides. Patriotic feel but don't overdo the flags.",
  },
  {
    label: "Holiday gift box",
    text: "Holiday gift box campaign. Show wrapped/presented seafood gift boxes — crab cakes, lobster tails, or combo boxes. Elegant, gift-giving mood. Target gift-shoppers buying for family.",
  },
  {
    label: '+ VIP40 offer',
    append: true,
    text: "INCLUDE THE VIP40 OFFER in every variant: $40 OFF the VIP Club with code VIP40 (first year $39.99 instead of $79.99; membership means 8% under market pricing, free UPS Ground shipping, free packaging and insurance). Mention code VIP40 in the ad copy. Every landing URL must carry ?cpoffer=vip40 so the $40 applies automatically at checkout, e.g. https://crabplace.com/maine-lobster/?cpoffer=vip40 or any product page with ?cpoffer=vip40 appended.",
  },
  {
    label: "Back in season",
    text: "Maryland Blue Crabs are back in season — limited summer window. Hero live or steamed hard crabs, emphasize freshness and Chesapeake sourcing. Urgency without being salesy.",
  },
];

// ── AI suggestion flow ───────────────────────────────────────────────────────
const SUGGEST_MODEL = 'claude-sonnet-4-6';
const SUGGEST_LOOP_MAX = 6;
const SITE_URL = 'https://cpx-prototype.netlify.app';

const SEARCH_TOOL = {
  name: 'search_media_library',
  description: "Search The Crab Place's classified library of product and lifestyle photography. Returns asset metadata including `id` (which you'll reference in propose_campaign), public URLs, alt_text, tags, quality_score (1-5), and `starred` (a boolean — Greg's hand-curated 'this is great' flag; starred images beat non-starred ones of the same quality). Call this 1-3 times to find candidates before proposing.",
  input_schema: {
    type: 'object',
    properties: {
      query: { type: 'string', description: 'Natural language search. Example: "steamed blue crabs bushel table"' },
      category: { type: 'string', enum: ['products', 'plated', 'lifestyle', 'ugc', 'process', 'people', 'marketing'] },
      subcategory: { type: 'string' },
      tags: { type: 'array', items: { type: 'string' } },
      minQuality: { type: 'integer', minimum: 1, maximum: 5, description: '4+ recommended for hero placements.' },
      starredOnly: { type: 'boolean', description: "true = only Greg's hand-starred favorites. Run your FIRST search with this on — if any starred asset fits the brief, use it over a non-starred one." },
      limit: { type: 'integer', minimum: 1, maximum: 20 },
    },
  },
};

const PROPOSE_CAMPAIGN_TOOL = {
  name: 'propose_campaign',
  description: "Submit the final campaign proposal. Call this exactly once at the end, after you've searched the Media Library and chosen creatives. Each variant references a real media_assets.id from your earlier search results.",
  input_schema: {
    type: 'object',
    properties: {
      campaignName: { type: 'string', description: 'Human-readable campaign name, e.g. "Father\'s Day Crab Cakes <currentYear>" — use the current year from the system prompt, not a hardcoded one.' },
      objective: { type: 'string', enum: ['OUTCOME_TRAFFIC', 'OUTCOME_SALES', 'OUTCOME_ENGAGEMENT', 'OUTCOME_AWARENESS', 'OUTCOME_LEADS'] },
      dailyBudgetUsd: { type: 'number', minimum: 5, description: 'Suggested daily budget in USD. Typical range 20-100 for a tactical push.' },
      audience: {
        type: 'object',
        properties: {
          countries: { type: 'array', items: { type: 'string' }, description: 'ISO country codes, e.g. ["US"]' },
          ageMin: { type: 'integer', minimum: 13, maximum: 65 },
          ageMax: { type: 'integer', minimum: 13, maximum: 65 },
          gender: { type: 'string', enum: ['all', 'men', 'women'], description: 'Gender targeting. Default "all" unless the theme clearly skews (e.g. Father\'s Day gift-buyers are often women shopping for men — think about who BUYS, not who receives).' },
          rationale: { type: 'string', description: 'One-line why-this-audience.' },
        },
      },
      landingUrl: { type: 'string', description: 'Full URL on crabplace.com the ad should drive to.' },
      rationale: { type: 'string', description: '1-2 sentences on the overall campaign angle.' },
      variants: {
        type: 'array',
        minItems: 4,
        maxItems: 10,
        items: {
          type: 'object',
          properties: {
            adName: { type: 'string', description: 'Short internal name, e.g. "Crab cakes hero v1"' },
            mediaAssetId: { type: 'integer', description: 'The media_assets.id from search_media_library results.' },
            headline: { type: 'string', description: 'Under 40 chars. Appears bold below the image.' },
            primaryText: { type: 'string', description: 'Under 125 chars. The main copy above the image.' },
            description: { type: 'string', description: 'Optional sub-line.' },
            ctaType: { type: 'string', enum: ['SHOP_NOW', 'LEARN_MORE', 'SIGN_UP', 'GET_OFFER', 'ORDER_NOW', 'SUBSCRIBE'] },
            placement: { type: 'string', enum: ['square', 'portrait', 'stories', 'link'], description: 'Primary placement this variant is designed for.' },
            rationale: { type: 'string', description: 'Why this creative + copy combo.' },
          },
          required: ['adName', 'mediaAssetId', 'headline', 'primaryText', 'ctaType', 'placement'],
        },
      },
    },
    required: ['campaignName', 'objective', 'dailyBudgetUsd', 'landingUrl', 'variants'],
  },
};

// Carousel proposals. Same campaign-level shape as PROPOSE_CAMPAIGN_TOOL, but a
// variant is a whole carousel ad: one primary text, 3-6 cards each with its own
// image and headline. Placement is not asked for - a carousel always ships square
// (see switchCreativeMode), so letting the model pick one would only be ignored.
const PROPOSE_CAROUSEL_TOOL = {
  name: 'propose_campaign',
  description: "Submit the final CAROUSEL campaign proposal. Call this exactly once at the end. Each variant is one carousel ad made of 3-6 cards, and every card references a real media_assets.id from your earlier search results.",
  input_schema: {
    type: 'object',
    properties: {
      campaignName: PROPOSE_CAMPAIGN_TOOL.input_schema.properties.campaignName,
      objective: PROPOSE_CAMPAIGN_TOOL.input_schema.properties.objective,
      dailyBudgetUsd: PROPOSE_CAMPAIGN_TOOL.input_schema.properties.dailyBudgetUsd,
      audience: PROPOSE_CAMPAIGN_TOOL.input_schema.properties.audience,
      landingUrl: PROPOSE_CAMPAIGN_TOOL.input_schema.properties.landingUrl,
      rationale: PROPOSE_CAMPAIGN_TOOL.input_schema.properties.rationale,
      variants: {
        type: 'array',
        minItems: 2,
        maxItems: 4,
        items: {
          type: 'object',
          properties: {
            adName: { type: 'string', description: 'Short internal name, e.g. "Feast story carousel v1"' },
            primaryText: { type: 'string', description: 'Under 125 chars. The copy above the carousel, shared by every card.' },
            ctaType: PROPOSE_CAMPAIGN_TOOL.input_schema.properties.variants.items.properties.ctaType,
            rationale: { type: 'string', description: 'Why this sequence of cards.' },
            cards: {
              type: 'array',
              minItems: 3,
              maxItems: 6,
              items: {
                type: 'object',
                properties: {
                  mediaAssetId: { type: 'integer', description: 'The media_assets.id from search_media_library results.' },
                  headline: { type: 'string', description: 'Under 32 chars. Bold line under this card.' },
                  description: { type: 'string', description: 'Optional short sub-line for this card.' },
                },
                required: ['mediaAssetId', 'headline'],
              },
            },
          },
          required: ['adName', 'primaryText', 'ctaType', 'cards'],
        },
      },
    },
    required: ['campaignName', 'objective', 'dailyBudgetUsd', 'landingUrl', 'variants'],
  },
};

const CAROUSEL_PROMPT_ADDENDUM = `

FORMAT: CAROUSEL. The user wants carousel ads, not single images. This overrides the variant and placement guidance above:
- Propose 2-4 variants. Each variant is ONE carousel ad of 3-6 cards the viewer swipes through.
- Every card needs its own image and its own short headline (under 32 chars). The primary text is shared by the whole carousel.
- Cards in one carousel should tell a sequence (the table, the crabs, the people, the offer) rather than repeat one shot. Every card still has to satisfy the brief.
- No image may appear twice inside the same carousel. Prefer different images across variants too, so the variants test different creative.
- Carousels always run square, so do not choose a placement.
- You will need more images than a single-image proposal, so plan on 2-4 searches.`;

const SUGGEST_SYSTEM_PROMPT = `You are a paid-social strategist for The Crab Place (crabplace.com), a Maryland seafood retailer. When the user describes a campaign theme, you:

1. Search the Media Library 1-4 times using search_media_library to find candidate creatives.
2. Compose 4-10 ad variants that test different angles — different audience hooks, creative directions, copy angles, offers, and placements. More variants = more learnings when the campaign is live, so lean toward 6-8 unless the brief is truly narrow.
3. Call propose_campaign exactly ONCE with the final proposal. Each variant must reference a real media_assets.id you saw in your search results.

Campaign guidelines:
- Voice: warm, celebratory, Maryland-proud. Never generic or salesy.
- Ad copy rules: headline under 40 chars; primary text under 125 chars. Evocative language is welcome ("real Maryland", "fresh off the dock", "the Bay", "crab feast"). NEVER use em dashes or en dashes in ad copy; use a period or a comma. Write number ranges as "1 to 2".
- Landing URL: default to a product category on crabplace.com. Example: /crab-cakes/, /hard-crabs/, /crab-meat/. Always include utm params will be added later — don't add them yourself.
- Audience: default US, ages 32-65 unless the theme says otherwise (e.g. "retirement gift box" → older, "young families" → 28-45 with parent interest targeting).
- Budget: 20-50 USD/day for a standard push, 75-150 for a hero launch.
- Placement variety: when proposing 3+ variants, use different placements — don't stack all on "square". Stories variants should be more visual, Link variants more copy-forward.

Brief parsing — this is the MOST IMPORTANT step, do it first:

Before any search, identify what the brief demands:
- **Hard subject requirements**: words like "show a woman", "featuring Dad", "Mom eating", "family", "couple", "group", "kids", "with a man" all mean a PERSON must be visible in every variant. This is non-negotiable. A plated-food shot with no person violates the brief, even if the food looks great.
- **Soft requirements**: "summer vibes", "Maryland heritage", "authentic" — these are mood/tone cues, not subject gates.
- **Product cues**: "crab cakes", "steamed crabs", "lobster tails" — ideally visible, but less strict than person requirements.

When the brief is person-centric:
- **Check Greg's starred picks FIRST.** Your first search_media_library call should be \`{ query: <2-3 core words>, starredOnly: true }\` — those are the images Greg hand-flagged as his best. If a starred image fits the brief, it wins over any non-starred image. Then search again without starredOnly to fill remaining variants.
- **Start broad, then narrow.** First search: 2-3 core words, NO category filter, NO quality floor. Just \`{ query: "family crabs" }\` or \`{ query: "group feast" }\`. The library indexes across categories; a "family-eating-crabs" photo might be in \`ugc\`, \`lifestyle\`, or even \`plated\` depending on how it was classified. Starting with a category filter risks excluding perfectly good matches.
- Only add \`category\` on follow-up searches when your first pass returned too many irrelevant results.
- Good initial search queries: "family crabs", "group dinner", "couple seafood", "outdoor feast", "bay table". Short. 2-3 concepts.
- Bad initial queries: "woman smiling eating seafood crabs at table" — too many stemmed terms; full-text search requires ALL to match.
- Before including an asset in the proposal, check: does alt_text or suggested_name mention a person? If not, it fails the brief. Reject it.
- Plated / product shots are SUPPORTING imagery, not hero creative for a people-centric brief. Do not hero them.

**Zero-result recovery.** If a search returns 0 results, the tool response will include a \`hint\` telling you exactly which constraint to relax next. Follow it — don't try another equally narrow query. Apply at least two relaxations if you zero-out twice in a row. If you zero-out three searches in a row despite relaxing, THEN the library genuinely lacks content for the brief.

When the brief is product-centric ("show off our crab cakes", "hero the snow crab"):
- Search \`category: "products"\` or \`category: "plated"\` first. People-centric images are supporting.

Creative selection within the qualified candidates:
- Prefer starred, then higher quality_score. Avoid quality_score = 1.
- Light anti-patterns (skip UNLESS the brief invites them): Thanksgiving-spread shots for non-holiday themes, off-center product shots, \`branded-packaging\` shots for appetite-focused ads.

**Worked example — Mother's Day push, "show a woman smiling with seafood":**
- WRONG: search "snow crab risotto elegant plated", pick three plated-food variants with no people. Doesn't satisfy "show a woman."
- RIGHT: search "woman eating crabs", "family feast with mom", "couple seafood dinner table"; pick lifestyle/ugc shots where a woman is visible (solo, with a partner, or in a family group). Even a multi-person family scene satisfies the brief if a woman is featured in it.

**Flexibility within the person requirement**: "can be with a man or a group" means you're allowed to broaden from solo-woman to couple/family scenes — it does NOT mean you're allowed to drop the person requirement entirely.

**Every variant must pass the brief, not just the first few.** Do not relapse to product/plated shots for variants 5-10 because you ran out of people shots on the first search. If you need more people shots, search again with a fresh query ("seafood party", "bay view dinner", "backyard crab feast"). A 5-variant person-centric proposal beats an 8-variant proposal where the last three are charcuterie boards.

**No duplicate images across variants — by ID OR by underlying content.** Each variant must reference a UNIQUE media_assets.id AND a unique underlying image. The library has some imports of the same photo under different IDs (near-duplicates from double imports); treat those as one asset. If you see two search results with identical-looking alt_text / suggested_name / tags, they're probably dupes — pick one, skip the rest. Never pad the variant count by running the same photo at multiple placements — a 4-variant proposal with 4 distinct images beats an 8-variant proposal where half are dupes.

Plain-text escape valve: use ONLY after 3+ varied people-centric searches find nothing usable. When you use it, tell the user exactly what you searched and what the library had instead. Don't use it just because your first narrow literal search failed.`;

// Proposals are cropped on a canvas, so a video can never stand in for a photo.
const isVideoAsset = (a) => a?.media_kind === 'video' || /\.(mp4|mov|m4v|webm)$/i.test(String(a?.r2_original_key || ''));

function buildImageUrl(asset) {
  const key = asset.r2_original_key || asset.r2_thumb_key;
  return `${SITE_URL}/.netlify/functions/r2-image?key=${encodeURIComponent(key)}`;
}

async function runSearchTool(input, seenAssets, brandOverride) {
  const { query = '', category, subcategory, tags, minQuality, limit = 8, starredOnly = false } = input || {};
  // brandOverride wins over whatever Claude passes in — when the user has
  // filtered to "Crab Place only", Claude cannot silently return Crab &
  // Cruise assets.
  const brand = brandOverride || input?.brand || null;
  const rows = await neonQuery('media_assets_list', {
    search: query || '',
    brand,
    category: category || null,
    subcategory: subcategory || null,
    minQuality: minQuality || 0,
    usableForAny: [],
    tagsAll: Array.isArray(tags) ? tags : [],
    excludeTags: [],  // HEIC uploads are stored as JPEG - 'source-heic' is provenance, not a format problem
    starredOnly: !!starredOnly,
    includeArchived: false,
    limit: Math.min(Math.max(1, limit), 20),
    offset: 0,
  });
  for (const a of (rows || [])) seenAssets.set(a.id, a);
  const mapped = (rows || []).map((r) => ({
    id: r.id,
    url: buildImageUrl(r),
    category: r.category,
    subcategory: r.subcategory,
    suggested_name: r.suggested_name,
    alt_text: r.alt_text,
    tags: r.tags || [],
    quality_score: r.quality_score,
    starred: !!r.human_starred,
    width: r.width || null,
    height: r.height || null,
  }));

  // Zero-result hint: tell Claude exactly which constraint to relax next.
  // Without this, Claude tends to retry with another equally-narrow query.
  if (mapped.length === 0) {
    const constraints = [];
    if (category)   constraints.push(`category='${category}'`);
    if (subcategory) constraints.push(`subcategory='${subcategory}'`);
    if (tags?.length) constraints.push(`tags=[${tags.join(',')}]`);
    if (minQuality)  constraints.push(`minQuality>=${minQuality}`);
    if (starredOnly) constraints.push('starredOnly=true (drop this to search the whole library)');
    if (brand)       constraints.push(`brand='${brand}' (enforced by user, cannot be relaxed)`);
    return {
      results: [],
      hint:
        `Zero results. Your next search should RELAX a constraint rather than try another equally narrow query.` +
        (constraints.length ? `\nFilters active on this search: ${constraints.join(', ')}.` : '') +
        `\nSuggested relaxations in order:\n` +
        `  1. DROP the category/subcategory filter — photos may be categorized unexpectedly; search on the query alone.\n` +
        `  2. DROP minQuality (or lower it to 2) — quality is a preference, not a gate.\n` +
        `  3. SHORTEN the query to 1-2 core concepts (e.g. "family crabs" instead of "woman smiling eating seafood crabs") — full-text search needs ALL stem-matched terms present.\n` +
        `  4. TRY synonyms ("feast" instead of "dinner", "gathering" instead of "party").\n` +
        `Apply at least TWO relaxations on your next call if you hit zero again.`,
    };
  }
  return mapped;
}

// Returns one of:
//   { proposal, messages, seenAssets }      — success
//   { bailText, messages, seenAssets }      — Claude returned text (follow up)
// Throws for genuine errors (network, API rejection, etc.) — the caller shows
// those as errors. Always returns the current `messages` + `seenAssets` so the
// caller can continue the conversation on bail.
// Read one of our landing pages server-side (landing-page-read.js). Returns the
// page copy plus the Media Library rows for the photos the page already shows.
async function readLandingPage(url) {
  const token = getAccessToken();
  const res = await fetch('/.netlify/functions/landing-page-read', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ url }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.error) throw new Error(data.error || `Could not read the page (HTTP ${res.status})`);
  return data;
}

// The landing page becomes the brief. The page copy is what the team already
// approved, so the ads restate it rather than invent a new offer.
function landingPromptBlock(page) {
  const photos = (page.assets || []).slice(0, 30).map((a) =>
    `  - id ${a.id}: ${a.alt_text || a.suggested_name || ''}${a.human_starred ? ' (starred)' : ''}`).join('\n');
  return `

LANDING PAGE. The ads drive to a page the team has already built and approved. It is the brief.
- URL: ${page.url}
- Every landingUrl you propose must be EXACTLY this URL. Do not substitute a category page.
- Write the ads from this page: the same offer, code, price, value, minimum spend, expiry and terms, in the page's own voice. Never promise anything the page does not say, and do not drop a condition the page states (a minimum spend, first order only, an expiry).
- Every factual claim must be in the page copy below: shipping speed, delivery options, grading, sourcing, ratings, percentages. If it is not there, leave it out, even if it sounds like us.
- State the offer exactly. If only one item is free, never say or imply the order, the meal or the table is free (no "try us free", no "the whole table eats free"). Keep the minimum spend next to the word free.
- Headings that read like internal page names (containing "DRAFT", "Facebook", "new customers") are admin labels, not copy. Ignore them.
- Photos already on the page are listed below with their media ids. Prefer them so the ad looks like the page it opens. You may search the library for more that fit the same offer.
${photos ? `\nPhotos on the page:\n${photos}\n` : ''}
Page title: ${page.title}
Page description: ${page.description || '(none)'}
Headings:
${(page.headings || []).join('\n')}

<landing_page_copy>
${page.text}
</landing_page_copy>${page.truncated ? '\n(Copy truncated; the offer is near the top.)' : ''}`;
}

// Every image a proposed variant uses: one for a single-image ad, one per card
// for a carousel.
function variantAssetIds(v) {
  return Array.isArray(v?.cards) ? v.cards.map((c) => c.mediaAssetId) : [v?.mediaAssetId];
}

async function runAiSuggestLoop(prompt, brandFilter = null, priorState = null, format = 'single', landing = null) {
  const seenAssets = priorState?.seenAssets || new Map();
  // The page's own photos are valid ids from the start, so Claude can propose them
  // without searching for them first.
  for (const a of (landing?.assets || [])) if (!seenAssets.has(a.id)) seenAssets.set(a.id, a);
  const isCarouselFormat = format === 'carousel';
  const proposeTool = isCarouselFormat ? PROPOSE_CAROUSEL_TOOL : PROPOSE_CAMPAIGN_TOOL;
  const formatAddendum = (isCarouselFormat ? CAROUSEL_PROMPT_ADDENDUM : '') + (landing ? landingPromptBlock(landing) : '');

  // Brand constraint gets appended to the system prompt as a hard rule so
  // Claude can't wander — plus runSearchTool enforces it at the call site.
  // Today's date is prepended so campaign names use the right year — without
  // it Claude defaults to its training-era year (e.g. "Father's Day 2025"
  // when it's actually 2026).
  const today = new Date();
  const todayStr = today.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  const currentYear = today.getFullYear();
  const datePrefix = `Today's date is ${todayStr}. The current year is ${currentYear} — use this in any campaign name that includes a year.\n\n`;
  const systemPrompt = brandFilter
    ? `${datePrefix}${SUGGEST_SYSTEM_PROMPT}${formatAddendum}\n\nHARD CONSTRAINT: the user has restricted this campaign to brand '${brandFilter}'. Every search_media_library call must include brand: '${brandFilter}'. Do not propose any asset from a different brand.`
    : `${datePrefix}${SUGGEST_SYSTEM_PROMPT}${formatAddendum}`;

  // When resuming from a prior bail, prior messages + the new user prompt.
  let messages = priorState?.messages
    ? [...priorState.messages, { role: 'user', content: prompt }]
    : [{ role: 'user', content: prompt }];

  for (let iter = 0; iter < SUGGEST_LOOP_MAX; iter++) {
    const data = await callClaude({
      model: SUGGEST_MODEL,
      max_tokens: 4000,
      system: systemPrompt,
      tools: [SEARCH_TOOL, proposeTool],
      messages,
    });
    const blocks = data.content || [];
    const toolUses = blocks.filter((b) => b.type === 'tool_use');

    // Did Claude call propose_campaign? That's the terminal tool — but
    // validate uniqueness before accepting, otherwise two "variants" using
    // the same mediaAssetId are really just one ad with duplicated copy.
    const proposal = toolUses.find((b) => b.name === 'propose_campaign');
    if (proposal) {
      const variants = proposal.input?.variants || [];
      // A single-image proposal must not reuse a photo across variants. A
      // carousel must not reuse one inside the same carousel; across carousels
      // it is allowed (the prompt asks for variety, but it is not a defect).
      const groups = isCarouselFormat
        ? variants.map((v) => variantAssetIds(v))
        : [variants.map((v) => v.mediaAssetId)];
      const dupIds = [...new Set(groups.flatMap((ids) => ids.filter((id, i) => ids.indexOf(id) !== i)))];

      // Also check for near-duplicates: different DB ids pointing at the same
      // underlying R2 object (the library has some imports of the same photo
      // under different source_paths). Use r2_original_key if present, else
      // fall back to r2_thumb_key.
      const keyOf = (id) => {
        const a = seenAssets.get(id);
        return a ? (a.r2_original_key || a.r2_thumb_key || null) : null;
      };
      const dupIdGroupsForContent = [];
      for (const ids of groups) {
        const contentKeys = ids.map(keyOf);
        const dupKeys = [...new Set(contentKeys.filter((k, i) => k && contentKeys.indexOf(k) !== i))];
        for (const k of dupKeys) dupIdGroupsForContent.push({ key: k, ids: ids.filter((id, i) => contentKeys[i] === k) });
      }

      if (dupIds.length || dupIdGroupsForContent.length) {
        const parts = [];
        if (dupIds.length) {
          parts.push(`IDs used more than once: ${dupIds.join(', ')}.`);
        }
        if (dupIdGroupsForContent.length) {
          parts.push(
            `These variants have different mediaAssetIds but reference the SAME underlying image (near-duplicates imported twice into the library). Treat them as the same asset — drop all but one:\n` +
            dupIdGroupsForContent.map((g) => `  - IDs ${g.ids.join(', ')} all point to ${g.key}`).join('\n')
          );
        }
        messages = [
          ...messages,
          { role: 'assistant', content: blocks },
          { role: 'user', content: [{
            type: 'tool_result',
            tool_use_id: proposal.id,
            content:
              (isCarouselFormat
                ? `Validation error: every card inside one carousel must use a UNIQUE image, unique both by mediaAssetId and by underlying R2 file.\n\n`
                : `Validation error: every variant must reference a UNIQUE image — unique both by mediaAssetId and by underlying R2 file.\n\n`) +
              parts.join('\n\n') +
              `\n\nCall propose_campaign again with distinct images per variant. If you don't have enough distinct candidates that fit the brief, search search_media_library once more with a fresh query — or propose fewer variants (minimum 4) with genuinely different images. Do NOT pad variant count by reusing the same photo across multiple placements.`,
            is_error: true,
          }] },
        ];
        continue;
      }
      // The landing page is not the model's to change.
      const input = landing ? { ...proposal.input, landingUrl: landing.url } : proposal.input;
      return { proposal: { ...input, format, landingPage: landing ? landing.url : null }, messages: [...messages, { role: 'assistant', content: blocks }], seenAssets };
    }

    if (toolUses.length === 0) {
      // Claude returned plain text — either the escape valve or a clarifying
      // question. Return both the text AND messages so the caller can show the
      // text while keeping the conversation alive for follow-up.
      const textBlock = blocks.find((b) => b.type === 'text');
      const msg = textBlock?.text?.trim() || 'Claude returned an empty response.';
      return { bailText: msg, messages: [...messages, { role: 'assistant', content: blocks }], seenAssets };
    }

    // Append assistant turn, then run each search and append results.
    messages = [...messages, { role: 'assistant', content: blocks }];
    const toolResults = [];
    for (const block of toolUses) {
      if (block.name !== 'search_media_library') {
        toolResults.push({ type: 'tool_result', tool_use_id: block.id, content: `Error: unknown tool ${block.name}`, is_error: true });
        continue;
      }
      try {
        const result = await runSearchTool(block.input || {}, seenAssets, brandFilter);
        let payload = JSON.stringify(result);
        if (payload.length > 20000) payload = payload.slice(0, 20000) + '\n... (truncated; narrow your search)';
        toolResults.push({ type: 'tool_result', tool_use_id: block.id, content: payload });
      } catch (e) {
        toolResults.push({ type: 'tool_result', tool_use_id: block.id, content: `Error: ${e.message}`, is_error: true });
      }
    }
    messages = [...messages, { role: 'user', content: toolResults }];
  }
  throw new Error(`AI suggestion loop hit the ${SUGGEST_LOOP_MAX}-iteration cap without a proposal`);
}

// Placeholder for platform/view combos that aren't wired up yet.
function ComingSoon({ platform, view }) {
  const isPaid = view === 'paid';
  const tiktok = platform === 'TikTok';
  return (
    <div className="card" style={{ padding: 30, textAlign: 'center', maxWidth: 660, margin: '20px auto' }}>
      <div style={{ fontSize: 34, marginBottom: 10 }}>{tiktok ? '🎵' : '📸'}</div>
      <h3 style={{ margin: '0 0 8px', color: 'var(--navy)', fontSize: 17 }}>{platform} {isPaid ? 'Ads' : 'Posts'} — coming soon</h3>
      <p style={{ fontSize: 13, color: 'var(--gray-500)', lineHeight: 1.65, maxWidth: 540, margin: '0 auto' }}>
        {tiktok
          ? 'TikTok needs a TikTok-for-Developers app with content/ads API approval (a 1 to 2 week audit) before posting or ads can run from here. It is on the roadmap as a later phase.'
          : 'Instagram organic posting needs a one-time token upgrade (the Instagram publishing permission) on your linked IG account. Once that is granted, you will compose IG posts right here. Heads up: Instagram captions cannot carry a clickable link, so these track engagement (likes, views, comments) rather than orders.'}
      </p>
    </div>
  );
}

// Collapsible section on the Performance tab — paid + organic live on ONE page
// (Greg's ask 2026-07-15), each behind its own expand/collapse. Open state
// persists per section in localStorage so the tab reopens how you left it.
function PerfSection({ id, title, subtitle, children, defaultOpen = true }) {
  const [open, setOpen] = useState(() => {
    try { const v = localStorage.getItem('smbPerf:' + id); return v == null ? defaultOpen : v === '1'; } catch (e) { return defaultOpen; }
  });
  const toggle = () => setOpen((o) => {
    const n = !o;
    try { localStorage.setItem('smbPerf:' + id, n ? '1' : '0'); } catch (e) { /* noop */ }
    return n;
  });
  return (
    <div style={{ border: '1px solid var(--gray-200)', borderRadius: 10, marginBottom: 18, background: '#fff' }}>
      <button type="button" onClick={toggle}
        style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '12px 16px', background: 'var(--gray-50)', border: 'none', borderBottom: open ? '1px solid var(--gray-200)' : 'none', borderRadius: open ? '10px 10px 0 0' : 10, cursor: 'pointer', fontFamily: 'Montserrat', textAlign: 'left' }}>
        <span style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 14.5, fontWeight: 800, color: 'var(--navy)' }}>{title}</span>
          <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>{subtitle}</span>
        </span>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--orange)', flexShrink: 0 }}>{open ? '▾ Collapse' : '▸ Expand'}</span>
      </button>
      {open && <div style={{ padding: 16 }}>{children}</div>}
    </div>
  );
}

export default function MetaBuilder({ navigateTo }) {
  const user = getCurrentUser();
  const roles = Array.isArray(user?.roles) ? user.roles : (user?.roles || '').split(',').map((r) => r.trim());
  const isAllowed = roles.includes('Admin') || roles.includes('Marketing') || roles.includes('Manager');

  // Platform (Facebook / Instagram / TikTok) and view (Paid / Organic). Both
  // persisted so a hard-refresh keeps you where you were.
  const [platform, _setPlatform] = useState(() => { try { return sessionStorage.getItem('mb_platform') || 'facebook'; } catch (e) { return 'facebook'; } });
  const setPlatform = (p) => { try { sessionStorage.setItem('mb_platform', p); } catch (e) { /* noop */ } _setPlatform(p); if (p !== 'facebook' && view === 'boost') setView('organic'); };
  const [view, _setView] = useState(() => { try { return sessionStorage.getItem('mb_view') || 'paid'; } catch (e) { return 'paid'; } });
  const setView = (v) => { try { sessionStorage.setItem('mb_view', v); } catch (e) { /* noop */ } _setView(v); };

  // Campaign / ad set
  const [campaignName, setCampaignName] = useState('');
  const [objective, setObjective] = useState('OUTCOME_SALES');
  const [dailyBudget, setDailyBudget] = useState('25.00');

  // Audience / targeting. Geo chips + audience include/exclude ride the Meta
  // targeting spec straight through meta-campaign-push. When no geo chips are
  // set, the countries field is the fallback (old behavior).
  const [countries, setCountries] = useState('US');
  const [ageMin, setAgeMin] = useState(32);
  const [ageMax, setAgeMax] = useState(65);
  const [gender, setGender] = useState('all');    // 'all' | 'men' | 'women' → Meta targeting.genders
  const [geoChips, setGeoChips] = useState([]);   // [{ key, name, type, radius? }]
  const [incAud, setIncAud] = useState([]);       // [{ id, name }] targeted custom/lookalike audiences
  const [excAud, setExcAud] = useState([]);       // [{ id, name }] excluded audiences

  // Ad content
  const [adName, setAdName] = useState('');
  const [primaryText, setPrimaryText] = useState('');
  const [headline, setHeadline] = useState('');
  const [description, setDescription] = useState('');
  const [ctaType, setCtaType] = useState('SHOP_NOW');

  // Destination + UTMs
  const [destinationUrl, setDestinationUrl] = useState('https://crabplace.com/');
  const [utmSource, setUtmSource] = useState('meta');
  const [utmMedium, setUtmMedium] = useState('paid_social');
  // Unique token so utm_content never collides between ads. Regenerated after push.
  const [uid, setUid] = useState(() => Math.random().toString(36).slice(2, 6));

  // Creative
  const [creativeMode, setCreativeMode] = useState('single');  // 'single' | 'carousel'
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [activePlacement, setActivePlacement] = useState('square');
  const [cropOffset, setCropOffset] = useState({ x: 0.5, y: 0.5 });
  const [pickerOpen, setPickerOpen] = useState(false);

  // Carousel cards: [{ asset, offset, headline, description, landingUrl }]
  // Each card carries its OWN crop offset, because a set of photos framed for one
  // subject rarely all sit centre — and every card is cropped to the same square.
  const [cards, setCards] = useState([]);
  const [cardPickerOpen, setCardPickerOpen] = useState(false);
  const [carouselOptimize, setCarouselOptimize] = useState(true);   // let Meta reorder by performance
  const [carouselEndCard, setCarouselEndCard] = useState(true);     // trailing Page card
  const isCarousel = creativeMode === 'carousel';

  // Push state
  const [pushing, setPushing] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  const [pushError, setPushError] = useState(null);

  // Preview + past-campaigns state. `previewData` is what the modal renders;
  // shape matches meta-organic's `paid-get` response but the modal is happy to
  // take an ad-hoc preview built from the local form or the current proposal,
  // so nothing has to be pushed to Meta before you can screenshot it.
  const [previewData, setPreviewData] = useState(null);
  // Open by default. First cut had this collapsed and the header alone was too
  // easy to miss between the AI box above and the campaign form below — Zach
  // reported he could not find the list at all. Open + auto-load means he sees
  // either the list or "nothing here yet" without a click.
  const [pastOpen, setPastOpen] = useState(true);
  const [pastList, setPastList] = useState(null);      // null = never loaded; [] = loaded, empty
  const [pastLoading, setPastLoading] = useState(false);
  const [pastError, setPastError] = useState(null);
  const [openingPast, setOpeningPast] = useState(null); // id being loaded

  // A soft Meta warning (Graph couldn't be reached, token missing) separate
  // from a hard error (backend 500). The list can still render local pushes
  // even when Meta is unhappy, so we show it as a note instead of blocking.
  const [pastMetaWarning, setPastMetaWarning] = useState(null);
  const loadPastCampaigns = async () => {
    setPastLoading(true); setPastError(null); setPastMetaWarning(null);
    try {
      const res = await organicFetch({
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'paid-list', limit: 50 }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setPastList(Array.isArray(data.pushes) ? data.pushes : []);
      if (data.metaError) setPastMetaWarning(data.metaError);
    } catch (e) {
      setPastError(e.message);
    } finally {
      setPastLoading(false);
    }
  };
  // Load the list the first time the paid-ads view is visible. Fires once via
  // the ref guard — MetaBuilder is the tab shell and re-renders on every view
  // switch, so a bare useEffect with [view] would refetch on every toggle.
  useEffect(() => {
    if (view === 'paid' && pastList === null && !pastLoading && !pastError) {
      loadPastCampaigns();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const openPastCampaign = async (id) => {
    setOpeningPast(id); setPastError(null);
    try {
      const res = await organicFetch({
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'paid-get', id }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      if (data.push) setPreviewData({ source: 'past', push: data.push });
    } catch (e) {
      setPastError(e.message);
    } finally {
      setOpeningPast(null);
    }
  };

  // ── Shared drafts ──────────────────────────────────────────────────────────
  // A draft is a snapshot of the whole builder: Claude's proposal (plus the
  // asset rows its images point at, so nobody has to re-run Suggest) and the
  // manual form. Stored in Neon `meta_campaign_drafts`; see neon.js. Saving
  // sends the version we loaded, so a teammate's newer save is never silently
  // overwritten — the server answers 409 and we ask what to do.
  const [drafts, setDrafts] = useState(null);           // list rows (no payload)
  const [draftsLoading, setDraftsLoading] = useState(false);
  const [draftsError, setDraftsError] = useState(null);
  const [currentDraft, setCurrentDraft] = useState(null); // { id, name, version, updated_by_name, updated_at }
  const [draftName, setDraftName] = useState('');
  const [draftSaving, setDraftSaving] = useState(false);
  const [draftMsg, setDraftMsg] = useState(null);       // { kind: 'ok'|'err'|'conflict', text, current? }
  const [openingDraft, setOpeningDraft] = useState(null);
  const lastSavedRef = useRef(null);                     // JSON of the payload as last saved/loaded
  const userName = user?.staffName || user?.email || null;

  const loadDrafts = async () => {
    setDraftsLoading(true); setDraftsError(null);
    try { setDrafts(await neonQuery('meta_drafts_list', {})); }
    catch (e) { setDraftsError(e.message); }
    finally { setDraftsLoading(false); }
  };
  useEffect(() => {
    if (view !== 'paid') return undefined;
    if (drafts === null && !draftsLoading) loadDrafts();
    // Quiet refresh so "Zach just saved" shows up without anyone clicking.
    const t = setInterval(() => { loadDrafts(); }, 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const serializeDraft = () => {
    // Only asset rows the proposal/form actually uses, plus a bounded slice of
    // what else Claude found so the ‹ › image cycler still has options.
    const used = new Set();
    (aiProposal?.variants || []).forEach((v) => {
      if (v.mediaAssetId != null) used.add(v.mediaAssetId);
      (v.cards || []).forEach((c) => c.mediaAssetId != null && used.add(c.mediaAssetId));
    });
    const all = Array.from(aiSeenAssets.values()).filter(Boolean);
    const assets = [...all.filter((a) => used.has(a.id)), ...all.filter((a) => !used.has(a.id)).slice(0, 40)];
    return {
      v: 1,
      ai: { prompt: aiPrompt, brand: aiBrand, format: aiFormat, landingUrl: aiLandingUrl, landing: aiLanding, proposal: aiProposal, assets },
      form: {
        campaignName, objective, dailyBudget, countries, ageMin, ageMax, gender, geoChips, incAud, excAud,
        adName, primaryText, headline, description, ctaType, destinationUrl, utmSource, utmMedium,
        creativeMode, selectedAsset, activePlacement, cropOffset, cards, carouselOptimize, carouselEndCard,
      },
    };
  };

  const summarizeDraft = (payload) => {
    const p = payload.ai.proposal;
    const f = payload.form;
    const firstAsset = p
      ? payload.ai.assets.find((a) => a.id === (p.variants?.[0]?.mediaAssetId ?? p.variants?.[0]?.cards?.[0]?.mediaAssetId))
      : (f.selectedAsset || f.cards?.[0]?.asset);
    return {
      source: p ? 'proposal' : 'form',
      format: p ? (p.format || 'single') : f.creativeMode,
      adCount: p ? (p.variants || []).length : ((f.selectedAsset || f.cards?.length) ? 1 : 0),
      dailyBudgetUsd: p ? p.dailyBudgetUsd : parseFloat(f.dailyBudget) || null,
      thumbKey: firstAsset?.r2_thumb_key || null,
    };
  };

  const hydrateDraft = (payload) => {
    const ai = payload?.ai || {};
    const f = payload?.form || {};
    setAiPrompt(ai.prompt || ''); setAiBrand(ai.brand ?? null); setAiFormat(ai.format || 'single');
    setAiLandingUrl(ai.landingUrl || ''); setAiLanding(ai.landing || null); setAiLandingWarning(null);
    setAiProposal(ai.proposal || null);
    setAiSeenAssets(new Map((ai.assets || []).map((a) => [a.id, a])));
    setAiMessages(null); setAiFollowUp(''); setAiError(null); setAiBailText(null);
    setCampaignName(f.campaignName || ''); setObjective(f.objective || 'OUTCOME_SALES'); setDailyBudget(f.dailyBudget || '25.00');
    setCountries(f.countries || 'US'); setAgeMin(f.ageMin ?? 32); setAgeMax(f.ageMax ?? 65); setGender(f.gender || 'all');
    setGeoChips(f.geoChips || []); setIncAud(f.incAud || []); setExcAud(f.excAud || []);
    setAdName(f.adName || ''); setPrimaryText(f.primaryText || ''); setHeadline(f.headline || '');
    setDescription(f.description || ''); setCtaType(f.ctaType || 'SHOP_NOW');
    setDestinationUrl(f.destinationUrl || 'https://crabplace.com/');
    setUtmSource(f.utmSource || 'meta'); setUtmMedium(f.utmMedium || 'paid_social');
    setCreativeMode(f.creativeMode || 'single'); setSelectedAsset(f.selectedAsset || null);
    setActivePlacement(f.activePlacement || 'square'); setCropOffset(f.cropOffset || { x: 0.5, y: 0.5 });
    setCards(f.cards || []); setCarouselOptimize(f.carouselOptimize ?? true); setCarouselEndCard(f.carouselEndCard ?? true);
    setPushResult(null); setPushError(null);
  };

  const hasUnsavedWork = () => {
    const now = JSON.stringify(serializeDraft());
    if (lastSavedRef.current) return now !== lastSavedRef.current;
    // No draft open: anything beyond an empty builder counts as work.
    return !!(aiProposal || campaignName || adName || primaryText || selectedAsset || cards.length);
  };

  const saveDraft = async ({ asNew = false, force = false } = {}) => {
    if (draftSaving) return;
    setDraftSaving(true); setDraftMsg(null);
    try {
      const payload = serializeDraft();
      const name = draftName.trim() || aiProposal?.campaignName || campaignName || 'Untitled draft';
      const rows = await neonQuery('meta_drafts_save', {
        id: asNew ? null : currentDraft?.id || null,
        version: asNew ? null : currentDraft?.version,
        name, payload, summary: summarizeDraft(payload), user_name: userName, force,
      });
      const row = rows[0];
      setCurrentDraft(row); setDraftName(row.name);
      lastSavedRef.current = JSON.stringify(payload);
      setDraftMsg({ kind: 'ok', text: `Saved "${row.name}" (v${row.version}). Your team can pick it up from Draft campaigns.` });
      loadDrafts();
    } catch (e) {
      if (e.status === 409) setDraftMsg({ kind: 'conflict', text: e.message, conflict: e.body?.conflict, current: e.body?.current });
      else setDraftMsg({ kind: 'err', text: e.message });
    } finally {
      setDraftSaving(false);
    }
  };

  const openDraft = async (id, { skipConfirm = false } = {}) => {
    if (!skipConfirm && currentDraft?.id !== id && hasUnsavedWork()
      && !window.confirm('You have unsaved changes in the builder. Open this draft anyway and discard them?')) return;
    setOpeningDraft(id); setDraftMsg(null);
    try {
      const rows = await neonQuery('meta_drafts_get', { id });
      const d = rows[0];
      hydrateDraft(d.payload);
      const { payload, ...meta } = d;
      setCurrentDraft(meta); setDraftName(d.name);
      lastSavedRef.current = JSON.stringify(payload);
      setDraftMsg({ kind: 'ok', text: `Opened "${d.name}" (v${d.version}, last saved by ${d.updated_by_name || 'unknown'}). Edit, then Save draft.` });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setDraftMsg({ kind: 'err', text: e.message });
    } finally {
      setOpeningDraft(null);
    }
  };

  const closeDraft = () => {
    if (hasUnsavedWork() && !window.confirm('Close this draft? Unsaved changes stay in the builder but will not be saved to the draft.')) return;
    setCurrentDraft(null); setDraftName(''); setDraftMsg(null); lastSavedRef.current = null;
  };

  const deleteDraft = async (d) => {
    if (!window.confirm(`Delete draft "${d.name}" for everyone?`)) return;
    try {
      await neonQuery('meta_drafts_set_status', { id: d.id, status: 'archived', user_name: userName });
      if (currentDraft?.id === d.id) { setCurrentDraft(null); setDraftName(''); lastSavedRef.current = null; }
      loadDrafts();
    } catch (e) { setDraftMsg({ kind: 'err', text: e.message }); }
  };

  // Every successful push lands here. If the push came from an open draft, the
  // draft is done: mark it pushed so it leaves the drafts list.
  const onPushed = (data) => {
    setPushResult(data); setUid(Math.random().toString(36).slice(2, 6));
    if (currentDraft?.id) {
      neonQuery('meta_drafts_set_status', { id: currentDraft.id, status: 'pushed', push_id: data?.pushId ?? data?.campaignId ?? null, user_name: userName })
        .then(() => { setCurrentDraft(null); setDraftName(''); lastSavedRef.current = null; loadDrafts(); })
        .catch(() => {});
    }
    loadPastCampaigns();
  };

  // Preview the current AI proposal without pushing anything. Reads the same
  // seenAssets the Push button reads, so what you screenshot is what would land.
  const previewProposal = () => {
    if (!aiProposal) return;
    const isCarouselProposal = aiProposal.format === 'carousel';
    const ads = (aiProposal.variants || []).map((v) => {
      if (isCarouselProposal) {
        const cards = (v.cards || [])
          .map((c) => ({ asset: aiSeenAssets.get(c.mediaAssetId), c }))
          .filter((x) => x.asset)
          .map(({ asset, c }) => ({
            imageUrl: `/.netlify/functions/r2-image?key=${encodeURIComponent(asset.r2_thumb_key)}`,
            headline: c.headline || null,
            description: c.description || null,
            landingUrl: c.landingUrl || aiProposal.landingUrl,
          }));
        return {
          name: v.adName, format: 'carousel',
          primaryText: v.primaryText, headline: null, description: null,
          ctaType: v.ctaType, landingUrl: aiProposal.landingUrl, cards,
        };
      }
      const asset = aiSeenAssets.get(v.mediaAssetId);
      return {
        name: v.adName, format: 'single',
        primaryText: v.primaryText, headline: v.headline || null,
        description: v.description || null, ctaType: v.ctaType,
        landingUrl: aiProposal.landingUrl,
        imageUrl: asset ? `/.netlify/functions/r2-image?key=${encodeURIComponent(asset.r2_thumb_key)}` : null,
      };
    });
    setPreviewData({
      source: 'proposal',
      push: {
        campaign_name: aiProposal.campaignName,
        objective: aiProposal.objective,
        daily_budget_cents: Math.round((aiProposal.dailyBudgetUsd || 25) * 100),
        ads,
        created_at: null,
      },
    });
  };

  // Preview the form as it currently stands, without pushing. Uses the same
  // crop preview URL as CropPreview so what you screenshot matches the crop.
  const previewCurrentForm = () => {
    if (isCarousel ? cards.length < 2 : !selectedAsset) return;
    const previewImg = (asset) => (asset ? `/.netlify/functions/r2-image?key=${encodeURIComponent(asset.r2_thumb_key || asset.r2_original_key)}` : null);
    const ad = isCarousel
      ? {
          name: adName || 'Untitled ad', format: 'carousel',
          primaryText, headline: null, description: null,
          ctaType, landingUrl: destinationUrl,
          cards: cards.map((c) => ({
            imageUrl: previewImg(c.asset),
            headline: c.headline || null,
            description: c.description || null,
            landingUrl: c.landingUrl || destinationUrl,
          })),
        }
      : {
          name: adName || 'Untitled ad', format: 'single',
          primaryText, headline, description, ctaType,
          landingUrl: destinationUrl,
          imageUrl: previewImg(selectedAsset),
        };
    setPreviewData({
      source: 'form',
      push: {
        campaign_name: campaignName || 'Untitled campaign',
        objective, daily_budget_cents: Math.round((parseFloat(dailyBudget) || 0) * 100),
        ads: [ad], created_at: null,
      },
    });
  };

  // AI suggestion flow
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiBrand, setAiBrand] = useState(null);   // null = both brands, 'crab-place', 'crab-and-cruise'
  const [aiFormat, setAiFormat] = useState('single');   // 'single' | 'carousel' - what Claude proposes
  const [aiLandingUrl, setAiLandingUrl] = useState('');   // our landing page the ads drive to
  const [aiLanding, setAiLanding] = useState(null);       // what landing-page-read returned for it
  const [aiLandingLoading, setAiLandingLoading] = useState(false);
  // A page we could not read. NOT an error: Claude still has the typed brief, so this
  // must never block Suggest the way a thrown read did until 2026-09-21.
  const [aiLandingWarning, setAiLandingWarning] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);       // genuine errors (network, API)
  const [aiBailText, setAiBailText] = useState(null); // Claude's actionable "nothing found / clarify" message
  const [aiMessages, setAiMessages] = useState(null); // conversation history so bail + follow-up is resumable
  const [aiFollowUp, setAiFollowUp] = useState('');   // text in the follow-up textarea
  const [aiProposal, setAiProposal] = useState(null);    // { campaignName, objective, dailyBudgetUsd, audience, landingUrl, rationale, variants }
  const [aiSeenAssets, setAiSeenAssets] = useState(new Map());  // id -> full asset row
  const [swapTarget, setSwapTarget] = useState(null);   // { variant, card } being swapped; card is null on a single-image variant
  const [appliedBanner, setAppliedBanner] = useState(null);   // flash confirmation after clicking "Use this variant"
  const formGridRef = useRef(null);

  // Read the landing page (once per URL) and point the form's destination at it.
  const loadLanding = async (force = false) => {
    const url = aiLandingUrl.trim();
    if (!url) { setAiLanding(null); return null; }
    if (!force && aiLanding && (aiLanding.requested === url || aiLanding.url === url)) return aiLanding;
    setAiLandingLoading(true);
    try {
      const page = { ...(await readLandingPage(url)), requested: url };
      setAiLandingWarning(null);
      setAiLanding(page);
      setDestinationUrl(page.url);
      return page;
    } finally {
      setAiLandingLoading(false);
    }
  };

  const runSuggest = async () => {
    const hasLanding = !!aiLandingUrl.trim();
    if ((!aiPrompt.trim() && !hasLanding) || aiLoading) return;
    setAiLoading(true); setAiError(null); setAiBailText(null); setAiProposal(null); setAiLandingWarning(null);
    setAiMessages(null);  // fresh run — drop any prior conversation
    try {
      // ⚠️ A PAGE WE CANNOT READ IS NOT A REASON TO REFUSE THE CAMPAIGN. readLandingPage
      // THROWS on an unreadable page, and this line used to let that abort the whole run
      // before Claude was ever called - so every crabandcruise.com URL (an Angular app,
      // whose text only exists after JS runs, so a server fetch sees ~18 characters) made
      // Suggest impossible. Claude still has the typed brief; degrade to that.
      let landing = null;
      if (hasLanding) {
        try {
          landing = await loadLanding();
        } catch (e) {
          setAiLandingWarning(e.message);
          // ⚠️ AND POINT THE ADS WHERE THEY ASKED. loadLanding only sets the destination on
          // success, so a failed read left it on its PREVIOUS value - the crabplace.com
          // default - while they were building a Crab & Cruise ad. Nothing on screen said so.
          setDestinationUrl(aiLandingUrl.trim());
        }
      }
      const brief = aiPrompt.trim() || 'Build a campaign for the landing page below. The offer, copy and audience all come from that page.';
      const { proposal, bailText, messages, seenAssets } =
        await runAiSuggestLoop(brief, aiBrand, null, aiFormat, landing);
      setAiMessages(messages); setAiSeenAssets(seenAssets);
      if (proposal) setAiProposal(proposal);
      else if (bailText) setAiBailText(bailText);
    } catch (e) {
      setAiError(e.message);
    } finally {
      setAiLoading(false);
    }
  };

  // Continue the conversation after Claude bailed with text. Uses the existing
  // messages + seenAssets so Claude has full context of what it already
  // searched and suggested.
  const sendFollowUp = async () => {
    const text = aiFollowUp.trim();
    if (!text || aiLoading || !aiMessages) return;
    setAiLoading(true); setAiError(null); setAiBailText(null);
    setAiFollowUp('');
    try {
      const { proposal, bailText, messages, seenAssets } =
        await runAiSuggestLoop(text, aiBrand, { messages: aiMessages, seenAssets: aiSeenAssets }, aiProposal?.format || aiFormat, aiLanding);
      setAiMessages(messages); setAiSeenAssets(seenAssets);
      if (proposal) setAiProposal(proposal);
      else if (bailText) setAiBailText(bailText);
    } catch (e) {
      setAiError(e.message);
    } finally {
      setAiLoading(false);
    }
  };

  // Fill the form with a variant's values + campaign-wide settings from the proposal.
  const applyVariant = (variant) => {
    if (!aiProposal) return;
    setCampaignName(aiProposal.campaignName || '');
    setObjective(aiProposal.objective || 'OUTCOME_SALES');
    if (aiProposal.dailyBudgetUsd) setDailyBudget(String(aiProposal.dailyBudgetUsd.toFixed(2)));
    if (aiProposal.audience) {
      if (Array.isArray(aiProposal.audience.countries) && aiProposal.audience.countries.length) {
        setCountries(aiProposal.audience.countries.join(', '));
      }
      if (aiProposal.audience.ageMin) setAgeMin(aiProposal.audience.ageMin);
      if (aiProposal.audience.ageMax) setAgeMax(aiProposal.audience.ageMax);
      if (aiProposal.audience.gender) setGender(aiProposal.audience.gender);
    }
    if (aiProposal.landingUrl) setDestinationUrl(aiProposal.landingUrl);

    setAdName(variant.adName || '');
    setPrimaryText(variant.primaryText || '');
    setCtaType(variant.ctaType || 'SHOP_NOW');

    if (Array.isArray(variant.cards)) {
      // Carousel: the cards become the carousel editor's cards, square crop.
      const next = variant.cards
        .map((c) => ({ c, asset: aiSeenAssets.get(c.mediaAssetId) }))
        .filter((x) => x.asset)
        .slice(0, MAX_CARDS)
        .map(({ c, asset }) => ({ ...newCard(asset), headline: c.headline || '', description: c.description || '' }));
      setCards(next);
      setHeadline(''); setDescription('');
      setActivePlacement('square');
      setCreativeMode('carousel');
    } else {
      setHeadline(variant.headline || '');
      setDescription(variant.description || '');
      if (variant.placement) setActivePlacement(variant.placement);
      const asset = aiSeenAssets.get(variant.mediaAssetId);
      if (asset) { setSelectedAsset(asset); setCropOffset({ x: 0.5, y: 0.5 }); }
      setCreativeMode('single');
    }
    // Flash a confirmation + scroll the form into view. The form lives below
    // the proposal, so scrollTo(top:0) was the wrong direction.
    setAppliedBanner(`Applied "${variant.adName}" — review crop and copy below, then push.`);
    setTimeout(() => setAppliedBanner(null), 4000);
    setTimeout(() => formGridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  // Bulk-push every variant in the current proposal as one campaign with
  // per-placement ad sets. Each variant gets cropped to its own placement's
  // ratio, so Stories variants actually serve as Stories, Link as Link, etc.
  const pushProposal = async () => {
    if (!aiProposal || pushing) return;
    setPushing(true); setPushError(null); setPushResult(null);
    try {
      if (aiProposal.format === 'carousel') {
        await pushCarouselProposal();
        return;
      }
      const variants = (aiProposal.variants || []).filter((v) => aiSeenAssets.get(v.mediaAssetId));
      if (!variants.length) throw new Error('No variants have resolvable assets');

      // Group by placement so we create one ad set per placement with the right
      // targeting. Variants that share a placement share an ad set.
      const placementKeys = Array.from(new Set(variants.map((v) => v.placement || 'square')));
      const adSets = placementKeys.map((key) => {
        const p = PLACEMENT_BY_KEY[key] || PLACEMENTS[0];
        return {
          placement: key,
          name: `${aiProposal.campaignName} — ${p.label}`,
          dailyBudgetCents: Math.round((aiProposal.dailyBudgetUsd || 25) * 100),
          targeting: { ...baseAudience(), ...p.targeting },
        };
      });

      // Crop each variant's asset to its placement ratio, then emit an ad
      // pointing at the matching adSetIndex.
      const ads = [];
      for (const v of variants) {
        const pKey = v.placement || 'square';
        const p = PLACEMENT_BY_KEY[pKey] || PLACEMENTS[0];
        const asset = aiSeenAssets.get(v.mediaAssetId);
        const imageBase64 = await renderCroppedJpegBase64(asset, p.ratio, { x: 0.5, y: 0.5 });
        const adSetIndex = placementKeys.indexOf(pKey);

        // Mirror the UTM construction from the single-push path but using the
        // variant's adName rather than the form's.
        let link = aiProposal.landingUrl || 'https://crabplace.com/';
        try {
          const u = new URL(link);
          if (utmSource)   u.searchParams.set('utm_source',   utmSource);
          if (utmMedium)   u.searchParams.set('utm_medium',   utmMedium);
          if (aiProposal.campaignName) u.searchParams.set('utm_campaign', slug(aiProposal.campaignName));
          u.searchParams.set('utm_content', `${slug(v.adName) || 'ad'}-${Math.random().toString(36).slice(2, 6)}`);
          link = u.toString();
        } catch {}

        ads.push({
          name: v.adName,
          adSetIndex,
          headline: v.headline,
          primaryText: v.primaryText,
          description: v.description,
          ctaType: v.ctaType,
          landingUrl: link,
          imageBase64,
        });
      }

      const res = await fetch('/.netlify/functions/meta-campaign-push', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campaign: { name: aiProposal.campaignName, objective: aiProposal.objective || 'OUTCOME_SALES' },
          adSets: adSets.map(({ placement, ...rest }) => rest),
          ads,
          createdBy: user?.staffName || user?.email || null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatPushError(data, res.status));
      onPushed(data);
      setTimeout(() => formGridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    } catch (e) {
      setPushError(e.message);
    } finally {
      setPushing(false);
    }
  };

  // UTM-tagged link for an ad built from the proposal (not the form).
  const proposalLink = (url, name, suffix) => {
    let link = url || 'https://crabplace.com/';
    try {
      const u = new URL(link);
      if (utmSource)   u.searchParams.set('utm_source',   utmSource);
      if (utmMedium)   u.searchParams.set('utm_medium',   utmMedium);
      if (aiProposal?.campaignName) u.searchParams.set('utm_campaign', slug(aiProposal.campaignName));
      const base = `${slug(name) || 'ad'}-${Math.random().toString(36).slice(2, 6)}`;
      u.searchParams.set('utm_content', suffix ? `${base}-${suffix}` : base);
      link = u.toString();
    } catch {}
    return link;
  };

  // Carousel proposal: one square ad set, one carousel ad per variant. A card
  // whose asset is missing is dropped; a variant left with under 2 cards is not a
  // carousel and is skipped rather than failing the whole push.
  const pushCarouselProposal = async () => {
    const square = PLACEMENT_BY_KEY.square;
    const ads = [];
    for (const v of (aiProposal.variants || [])) {
      const live = (v.cards || []).filter((c) => aiSeenAssets.get(c.mediaAssetId)).slice(0, MAX_CARDS);
      if (live.length < 2) continue;
      const base = proposalLink(aiProposal.landingUrl, v.adName);
      const cardsOut = [];
      for (let i = 0; i < live.length; i++) {
        const c = live[i];
        cardsOut.push({
          imageBase64: await renderCroppedJpegBase64(aiSeenAssets.get(c.mediaAssetId), square.ratio, { x: 0.5, y: 0.5 }),
          headline: c.headline || '',
          description: c.description || '',
          landingUrl: (() => { try { const u = new URL(base); u.searchParams.set('utm_content', `${u.searchParams.get('utm_content')}-card${i + 1}`); return u.toString(); } catch { return base; } })(),
        });
      }
      ads.push({
        name: v.adName,
        adSetIndex: 0,
        headline: '',
        primaryText: v.primaryText,
        ctaType: v.ctaType,
        landingUrl: base,
        cards: cardsOut,
        multiShareOptimized: true,
        multiShareEndCard: true,
      });
    }
    if (!ads.length) throw new Error('No carousel has at least 2 usable images. Swap in images and try again.');
    const res = await fetch('/.netlify/functions/meta-campaign-push', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        campaign: { name: aiProposal.campaignName, objective: aiProposal.objective || 'OUTCOME_SALES' },
        adSets: [{
          name: `${aiProposal.campaignName} — Carousel`,
          dailyBudgetCents: Math.round((aiProposal.dailyBudgetUsd || 25) * 100),
          targeting: { ...baseAudience(), ...square.targeting },
        }],
        ads,
        createdBy: user?.staffName || user?.email || null,
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(formatPushError(data, res.status));
    onPushed(data);
    setTimeout(() => formGridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  // Replace one image in the proposal (a single-image variant, or one carousel
  // card) without regenerating anything.
  const setProposalAsset = (target, newAsset) => {
    setAiProposal((prev) => {
      if (!prev) return prev;
      const variants = prev.variants.map((v, i) => {
        if (i !== target.variant) return v;
        if (target.card == null) return { ...v, mediaAssetId: newAsset.id };
        return { ...v, cards: v.cards.map((c, j) => (j === target.card ? { ...c, mediaAssetId: newAsset.id } : c)) };
      });
      return { ...prev, variants };
    });
    setAiSeenAssets((prev) => {
      const next = new Map(prev);
      next.set(newAsset.id, newAsset);
      return next;
    });
  };
  const swapVariantAsset = (target, newAsset) => { setProposalAsset(target, newAsset); setSwapTarget(null); };

  // Step to the next (or previous) photo Claude already found, skipping any the
  // proposal already uses where a repeat would be a defect: another single-image
  // variant, or another card in the same carousel. No picker, one click.
  const cycleVariantAsset = (target, dir) => {
    if (!aiProposal) return;
    const pool = Array.from(aiSeenAssets.values()).filter((a) => a && a.r2_thumb_key && !isVideoAsset(a));
    if (!pool.length) return;
    const v = aiProposal.variants[target.variant];
    const currentId = target.card == null ? v.mediaAssetId : v.cards[target.card].mediaAssetId;
    const taken = new Set(target.card == null
      ? aiProposal.variants.filter((_, i) => i !== target.variant).map((x) => x.mediaAssetId)
      : v.cards.filter((_, j) => j !== target.card).map((c) => c.mediaAssetId));
    const start = Math.max(0, pool.findIndex((a) => a.id === currentId));
    for (let step = 1; step <= pool.length; step++) {
      const cand = pool[(start + dir * step + pool.length * step) % pool.length];
      if (cand.id !== currentId && !taken.has(cand.id)) { setProposalAsset(target, cand); return; }
    }
  };

  // Stamp our UTMs onto any destination. `contentSuffix` distinguishes the click
  // WITHIN one ad — on a carousel that's the card number, which is the only way to
  // tell afterwards which card people actually clicked.
  const withUtms = useMemo(() => (url, contentSuffix) => {
    if (!url) return '';
    try {
      const u = new URL(url);
      if (utmSource)   u.searchParams.set('utm_source',   utmSource);
      if (utmMedium)   u.searchParams.set('utm_medium',   utmMedium);
      if (campaignName) u.searchParams.set('utm_campaign', slug(campaignName));
      const base = `${slug(adName) || 'ad'}-${uid}`;
      u.searchParams.set('utm_content', contentSuffix ? `${base}-${contentSuffix}` : base);
      return u.toString();
    } catch { return url; }
  }, [utmSource, utmMedium, campaignName, adName, uid]);

  const landingUrlWithUtms = useMemo(() => withUtms(destinationUrl), [withUtms, destinationUrl]);

  // ── Carousel card management ───────────────────────────────────────────────
  const MAX_CARDS = 10;   // Meta's ceiling. Below 2 it isn't a carousel.

  const newCard = (asset) => ({ asset, offset: { x: 0.5, y: 0.5 }, headline: '', description: '', landingUrl: '' });

  const addCards = (assets) => {
    setCards((prev) => {
      // Same photo twice in one carousel is always a mistake, and it costs a card
      // slot out of ten. Skip duplicates rather than silently accepting them.
      const have = new Set(prev.map((c) => c.asset.id));
      const fresh = assets.filter((a) => !have.has(a.id)).map(newCard);
      return [...prev, ...fresh].slice(0, MAX_CARDS);
    });
    setCardPickerOpen(false);
  };
  const updateCard = (i, patch) => setCards((prev) => prev.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const removeCard = (i) => setCards((prev) => prev.filter((_, j) => j !== i));
  const moveCard = (i, dir) => setCards((prev) => {
    const j = i + dir;
    if (j < 0 || j >= prev.length) return prev;
    const next = [...prev];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });

  // Switching mode carries the creative across so the work already done isn't lost.
  const switchCreativeMode = (mode) => {
    if (mode === creativeMode) return;
    if (mode === 'carousel') {
      // Carousel renders square on every placement that supports it, and
      // right-hand-column doesn't support it at all — so pin to the square
      // placement rather than letting a 9:16 crop get letterboxed by Meta.
      setActivePlacement('square');
      if (!cards.length && selectedAsset) setCards([{ ...newCard(selectedAsset), offset: cropOffset, headline, description }]);
    } else if (!selectedAsset && cards.length) {
      setSelectedAsset(cards[0].asset);
      setCropOffset(cards[0].offset);
    }
    setCreativeMode(mode);
  };

  const canPush = (isCarousel ? cards.length >= 2 : !!selectedAsset)
    && !!campaignName && !!adName && !!primaryText && !!destinationUrl && !pushing;

  const baseAudience = () => {
    // Meta rejects age_max > 65 (65 = "65+") — clamp so a typo can't fail the push
    const aMin = Math.min(65, Math.max(13, parseInt(ageMin, 10) || 18));
    const aMax = Math.min(65, Math.max(aMin, parseInt(ageMax, 10) || 65));
    const t = { age_min: aMin, age_max: aMax };
    // Meta genders: [1] = men, [2] = women; omit the field entirely for all
    if (gender === 'men') t.genders = [1];
    else if (gender === 'women') t.genders = [2];
    if (geoChips.length) {
      const geo = {};
      geoChips.forEach((g) => {
        if (g.type === 'city') (geo.cities = geo.cities || []).push({ key: g.key, radius: g.radius || 25, distance_unit: 'mile' });
        else if (g.type === 'region') (geo.regions = geo.regions || []).push({ key: g.key });
        else if (g.type === 'zip') (geo.zips = geo.zips || []).push({ key: g.key });
        else if (g.type === 'geo_market') (geo.geo_markets = geo.geo_markets || []).push({ key: g.key });
        else if (g.type === 'country') (geo.countries = geo.countries || []).push(g.key);
      });
      t.geo_locations = geo;
    } else {
      t.geo_locations = { countries: countries.split(',').map((c) => c.trim().toUpperCase()).filter(Boolean) };
    }
    if (incAud.length) t.custom_audiences = incAud.map((a) => ({ id: a.id }));
    if (excAud.length) t.excluded_custom_audiences = excAud.map((a) => ({ id: a.id }));
    return t;
  };

  const push = async () => {
    if (!canPush) return;
    setPushing(true); setPushError(null); setPushResult(null);
    try {
      // A carousel always ships square — see switchCreativeMode.
      const placement = isCarousel ? PLACEMENT_BY_KEY.square : (PLACEMENT_BY_KEY[activePlacement] || PLACEMENTS[0]);
      const budgetCents = Math.round(parseFloat(dailyBudget || '0') * 100);
      const targeting = { ...baseAudience(), ...placement.targeting };

      const ad = {
        name: adName,
        adSetIndex: 0,
        headline,
        primaryText,
        description,
        ctaType,
        landingUrl: landingUrlWithUtms,
      };

      if (isCarousel) {
        // Crop every card to the SAME square. Meta letterboxes a mixed-ratio set
        // rather than rejecting it, so the ratio has to be right on the way out.
        ad.cards = [];
        for (let i = 0; i < cards.length; i++) {
          const c = cards[i];
          ad.cards.push({
            imageBase64: await renderCroppedJpegBase64(c.asset, placement.ratio, c.offset),
            headline: c.headline || '',
            description: c.description || '',
            // Every card gets its own utm_content so clicks are attributable per
            // card. A card with no link of its own still points at the ad's
            // destination — but tagged as that card, not as the ad.
            landingUrl: withUtms(c.landingUrl || destinationUrl, `card${i + 1}`),
          });
        }
        ad.multiShareOptimized = carouselOptimize;
        ad.multiShareEndCard = carouselEndCard;
      } else {
        ad.imageBase64 = await renderCroppedJpegBase64(selectedAsset, placement.ratio, cropOffset);
      }

      const res = await fetch('/.netlify/functions/meta-campaign-push', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campaign: { name: campaignName, objective },
          adSets: [{
            name: `${campaignName} — ${isCarousel ? 'Carousel' : placement.label}`,
            dailyBudgetCents: budgetCents,
            targeting,
          }],
          ads: [ad],
          createdBy: user?.staffName || user?.email || null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatPushError(data, res.status));
      onPushed(data);
    } catch (e) {
      setPushError(e.message);
    } finally {
      setPushing(false);
    }
  };

  if (!isAllowed) {
    return (
      <div style={{ padding: 24 }}>
        <h1 style={{ color: 'var(--navy)', fontWeight: 700 }}>Meta Campaign Builder</h1>
        <p style={{ color: 'var(--gray-500)' }}>You need Admin, Manager, or Marketing access.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 1280, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 14 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--navy)' }}>Social Media</h1>
        <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
          Build paid ads and organic posts across your social channels.
        </div>
      </div>

      {/* Platform tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {[{ k: 'facebook', label: 'Facebook', color: '#1877F2' }, { k: 'instagram', label: 'Instagram', color: '#E1306C' }, { k: 'tiktok', label: 'TikTok', color: '#111' }, { k: 'bitly', label: 'Bitly', color: '#EE6123' }, { k: 'calendar', label: 'Calendar', color: 'var(--navy)' }, { k: 'performance', label: 'Performance', color: 'var(--navy)' }].map(p => (
          <button key={p.k} onClick={() => setPlatform(p.k)}
            style={{ padding: '7px 18px', fontSize: 13, fontWeight: 700, fontFamily: 'Montserrat', borderRadius: 20, cursor: 'pointer',
              border: `1px solid ${platform === p.k ? p.color : 'var(--gray-200)'}`, background: platform === p.k ? p.color : '#fff', color: platform === p.k ? '#fff' : 'var(--gray-500)' }}>
            {p.label}
          </button>
        ))}
      </div>

      {/* Paid / Organic tabs (not for Bitly or the unified Calendar/Performance tabs) */}
      {platform !== 'bitly' && platform !== 'calendar' && platform !== 'performance' && (
        <div style={{ display: 'flex', gap: 4, marginBottom: 18, borderBottom: '1px solid var(--gray-200)' }}>
          {[{ k: 'paid', label: 'Paid Ads' }, { k: 'organic', label: 'Organic Posts' }, ...(platform === 'facebook' ? [{ k: 'boost', label: 'Viral Boost' }] : [])].map(t => (
            <button key={t.k} onClick={() => setView(t.k)} style={{ padding: '8px 18px', fontSize: 13, fontWeight: 700, fontFamily: 'Montserrat', border: 'none', background: 'none', cursor: 'pointer', color: view === t.k ? 'var(--orange)' : 'var(--gray-500)', borderBottom: view === t.k ? '2px solid var(--orange)' : '2px solid transparent', marginBottom: -1 }}>{t.label}</button>
          ))}
        </div>
      )}

      {/* Unified cross-platform tabs (Facebook + Instagram + TikTok combined) */}
      {platform === 'calendar' && <UnifiedCalendar />}
      {platform === 'performance' && (
        <>
          <PerfSection id="paid" title="Paid ads" subtitle="Meta campaigns — spend, funnel, our orders vs Meta's, attributed orders">
            <MetaCampaigns embedded navigateTo={navigateTo} />
          </PerfSection>
          <PerfSection id="organic" title="Organic posts" subtitle="Facebook & Instagram — engagement, clicks, carts, orders">
            <UnifiedPerformance />
          </PerfSection>
        </>
      )}

      {/* Bitly — standalone crabplace.co link generator (no paid/organic sub-tabs) */}
      {platform === 'bitly' && <><LinkBuilder /><PartnerLinks /></>}

      {/* TikTok — organic: connect + (soon) post. Paid: still gated. */}
      {platform === 'tiktok' && view === 'organic' && <TikTokBuilder />}
      {platform === 'tiktok' && view === 'paid' && <ComingSoon platform="TikTok" view="paid" />}

      {/* Instagram organic — live (Phase 2). */}
      {platform === 'instagram' && view === 'organic' && <InstagramBuilder />}

      {/* Facebook organic — live. */}
      {platform === 'facebook' && view === 'organic' && <OrganicBuilder />}

      {/* Facebook Viral Boost — which viral videos to pay to boost as top of funnel. */}
      {platform === 'facebook' && view === 'boost' && <ViralBoost />}

      {/* Paid ads — Facebook + Instagram share the Meta paid builder. */}
      {view === 'paid' && platform !== 'tiktok' && platform !== 'bitly' && platform !== 'calendar' && platform !== 'performance' && (<>
      {platform === 'instagram' && (
        <div style={{ marginBottom: 14, padding: '10px 14px', background: '#fdeef4', border: '1px solid #f3c9dc', borderRadius: 8, fontSize: 12, color: '#a83265' }}>
          <strong>Instagram ads run through Meta.</strong> This is the same Meta campaign builder — it places your ads on Facebook <em>and</em> Instagram. For an Instagram-first look, lean on the <strong>Stories</strong> and <strong>Portrait</strong> placements below.
        </div>
      )}
      <AISuggestPanel
        prompt={aiPrompt} setPrompt={setAiPrompt}
        brand={aiBrand} setBrand={setAiBrand}
        loading={aiLoading} error={aiError} bailText={aiBailText}
        followUp={aiFollowUp} setFollowUp={setAiFollowUp}
        onFollowUp={sendFollowUp}
        proposal={aiProposal} seenAssets={aiSeenAssets}
        onSuggest={runSuggest} onApply={applyVariant}
        onClear={() => {
          setAiProposal(null); setAiError(null); setAiBailText(null);
          setAiMessages(null); setAiFollowUp(''); setAiLandingWarning(null);
        }}
        format={aiFormat} setFormat={setAiFormat}
        landingUrl={aiLandingUrl} setLandingUrl={(v) => { setAiLandingUrl(v); setAiLandingWarning(null); if (aiLanding && v.trim() !== aiLanding.requested) setAiLanding(null); }}
        landing={aiLanding} landingLoading={aiLandingLoading} landingWarning={aiLandingWarning}
        onReadLanding={() => loadLanding(true).catch((e) => {
          setAiLandingWarning(e.message);
          setDestinationUrl(aiLandingUrl.trim());
        })}
        onSwap={(target) => setSwapTarget(target)}
        onCycle={cycleVariantAsset}
        onPushAll={pushProposal}
        onPreview={previewProposal}
        pushing={pushing}
      />

      <DraftsPanel
        drafts={drafts} loading={draftsLoading} error={draftsError}
        current={currentDraft} name={draftName} setName={setDraftName}
        saving={draftSaving} msg={draftMsg} openingId={openingDraft}
        onRefresh={loadDrafts}
        onSave={() => saveDraft()}
        onSaveAsNew={() => saveDraft({ asNew: true })}
        onOverwrite={() => saveDraft({ force: true })}
        onReloadTheirs={() => currentDraft && openDraft(currentDraft.id, { skipConfirm: true })}
        onOpen={(id) => openDraft(id)}
        onClose={closeDraft}
        onDelete={deleteDraft}
      />

      <PastCampaignsPanel
        open={pastOpen}
        onToggle={() => {
          const willOpen = !pastOpen;
          setPastOpen(willOpen);
          if (willOpen && pastList === null && !pastLoading) loadPastCampaigns();
        }}
        list={pastList}
        loading={pastLoading}
        error={pastError}
        metaWarning={pastMetaWarning}
        openingId={openingPast}
        onRefresh={loadPastCampaigns}
        onOpen={openPastCampaign}
      />

      {swapTarget !== null ? (
        <MediaPicker
          imagesOnly
          onClose={() => setSwapTarget(null)}
          onPick={(asset) => swapVariantAsset(swapTarget, asset)}
        />
      ) : null}

      {appliedBanner ? (
        <div style={{ marginBottom: 16, padding: '10px 14px', background: '#f0f9f4', border: '1px solid #b7e4c7', borderRadius: 6, fontSize: 13, color: '#1a7f37', display: 'flex', alignItems: 'center', gap: 8 }}>
          <CheckCircle2 size={16} /> {appliedBanner}
        </div>
      ) : null}

      <div ref={formGridRef} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, scrollMarginTop: 20 }}>
        {/* Left: copy + config */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <Card title="Campaign">
            <Field label="Campaign name">
              <input type="text" value={campaignName} onChange={(e) => setCampaignName(e.target.value)}
                placeholder="Father's Day Crab Cakes" style={inputStyle} />
            </Field>
            <Field label="Objective">
              <select value={objective} onChange={(e) => setObjective(e.target.value)} style={inputStyle}>
                {OBJECTIVES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
            <Field label="Daily budget (USD)">
              <input type="number" step="0.01" min="1" value={dailyBudget} onChange={(e) => setDailyBudget(e.target.value)}
                style={inputStyle} />
            </Field>
          </Card>

          <TargetingCard
            geoChips={geoChips} setGeoChips={setGeoChips}
            incAud={incAud} setIncAud={setIncAud}
            excAud={excAud} setExcAud={setExcAud}
            countries={countries} setCountries={setCountries}
            ageMin={ageMin} setAgeMin={setAgeMin}
            ageMax={ageMax} setAgeMax={setAgeMax}
            gender={gender} setGender={setGender}
          />

          <Card title="Ad copy">
            <Field label="Ad name (internal)">
              <input type="text" value={adName} onChange={(e) => setAdName(e.target.value)}
                placeholder="Crab Cakes square v1" style={inputStyle} />
            </Field>
            <Field label={`Primary text (${primaryText.length} / 125 recommended)`}>
              <textarea rows={3} value={primaryText} onChange={(e) => setPrimaryText(e.target.value)}
                placeholder="Send Dad real Maryland lump crab cakes…" style={{ ...inputStyle, resize: 'vertical' }} />
            </Field>
            <Field label={`Headline (${headline.length} / 40 recommended)`}>
              <input type="text" value={headline} onChange={(e) => setHeadline(e.target.value)}
                placeholder="$10 off Maryland crab cakes" style={inputStyle} />
            </Field>
            <Field label="Description (optional)">
              <input type="text" value={description} onChange={(e) => setDescription(e.target.value)}
                placeholder="Free shipping over $100" style={inputStyle} />
            </Field>
            <Field label="Call to action">
              <select value={ctaType} onChange={(e) => setCtaType(e.target.value)} style={inputStyle}>
                {CTA_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
              </select>
            </Field>
          </Card>

          <Card title="Destination + UTMs">
            <Field label="Landing URL">
              <input type="url" value={destinationUrl} onChange={(e) => setDestinationUrl(e.target.value)} style={inputStyle} />
            </Field>
            <div style={{ display: 'flex', gap: 10 }}>
              <Field label="utm_source" style={{ flex: 1 }}>
                <input type="text" value={utmSource} onChange={(e) => setUtmSource(e.target.value)} style={inputStyle} />
              </Field>
              <Field label="utm_medium" style={{ flex: 1 }}>
                <input type="text" value={utmMedium} onChange={(e) => setUtmMedium(e.target.value)} style={inputStyle} />
              </Field>
            </div>
            <div style={{ fontSize: 11, color: 'var(--gray-500)', wordBreak: 'break-all', background: '#f5f5f7', padding: 10, borderRadius: 4, marginTop: 6, fontFamily: 'monospace', lineHeight: 1.5 }}>
              {landingUrlWithUtms || <span style={{ fontStyle: 'italic' }}>Enter a landing URL to preview…</span>}
            </div>
            {destinationUrl ? (
              destinationIsTracked(destinationUrl) ? (
                <div style={{ marginTop: 8, padding: '8px 10px', background: '#f0f9f4', border: '1px solid #b7e4c7', borderRadius: 4, fontSize: 11.5, color: '#1a7f37', display: 'flex', alignItems: 'center', gap: 6, lineHeight: 1.4 }}>
                  <CheckCircle2 size={13} style={{ flexShrink: 0 }} />
                  <span><strong>Tracked.</strong> The tool fills in the UTMs for you — orders from this ad show up automatically in Marketing → Meta Campaigns → Attributed Orders.</span>
                </div>
              ) : (
                <div style={{ marginTop: 8, padding: '8px 10px', background: '#fff8ef', border: '1px solid #ffd9a8', borderRadius: 4, fontSize: 11.5, color: '#9a5b00', display: 'flex', alignItems: 'center', gap: 6, lineHeight: 1.4 }}>
                  <AlertCircle size={13} style={{ flexShrink: 0 }} />
                  <span><strong>Won't be tracked.</strong> This page isn't on crabplace.com, so we can't match its orders. Point the ad at a crabplace.com page to keep attribution working.</span>
                </div>
              )
            ) : null}
          </Card>
        </div>

        {/* Right: creative + push */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <Card title="Creative">
            {/* Single image vs carousel. A carousel is 2-10 images the viewer
                swipes through, each with its own headline and its own link. */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
              <button onClick={() => switchCreativeMode('single')} style={placementBtn(!isCarousel)}>Single image</button>
              <button onClick={() => switchCreativeMode('carousel')} style={placementBtn(isCarousel)}>Carousel</button>
            </div>

            <ShapeGuidance isCarousel={isCarousel} activePlacement={activePlacement} />

            {isCarousel ? (
              <CarouselEditor
                cards={cards}
                maxCards={MAX_CARDS}
                onAdd={() => setCardPickerOpen(true)}
                onUpdate={updateCard}
                onRemove={removeCard}
                onMove={moveCard}
                optimize={carouselOptimize}
                setOptimize={setCarouselOptimize}
                endCard={carouselEndCard}
                setEndCard={setCarouselEndCard}
              />
            ) : selectedAsset ? (
              <>
                {/* The advice sits ON the control, not only in the note above it —
                    the choice is made here, and a paragraph three inches away
                    gets skipped. Ordered best-for-mobile first. */}
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                  {[...PLACEMENTS].sort((a, b) => (a.rank || 9) - (b.rank || 9)).map((p) => {
                    const active = activePlacement === p.key;
                    return (
                      <button key={p.key} onClick={() => setActivePlacement(p.key)}
                        style={{ ...placementBtn(active), textAlign: 'left', lineHeight: 1.25, padding: '5px 9px' }}>
                        <div>{p.label}{p.rank === 1 && !active ? <span style={{ color: 'var(--orange)' }}> ★</span> : null}</div>
                        <div style={{ fontSize: 9.5, fontWeight: 500, opacity: active ? 0.8 : 0.6 }}>{p.hint}</div>
                      </button>
                    );
                  })}
                </div>
                <CropPreview asset={selectedAsset} placement={PLACEMENTS.find((p) => p.key === activePlacement)} offset={cropOffset} setOffset={setCropOffset} />
                {selectedAsset.media_kind === 'video' ? (
                  <div style={{ marginTop: 8, padding: '8px 10px', background: '#fff4e8', border: '1px solid #ffd9b0', borderRadius: 6, fontSize: 11.5, color: '#9a5b00', lineHeight: 1.45 }}>
                    You picked a <strong>video</strong>. This paid ad runs as a <strong>still image</strong> using the frame shown above (its poster). To run the actual playing video, post it from the <strong>Organic Posts</strong> tab. (Paid video ads can be added here if you want them.)
                  </div>
                ) : null}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
                  <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>
                    <Crop size={11} style={{ verticalAlign: 'middle' }} /> Drag the preview to adjust the crop center.
                  </div>
                  <button onClick={() => setPickerOpen(true)} style={secondaryBtn}>Replace image</button>
                </div>
              </>
            ) : (
              <div style={{ padding: '40px 20px', border: '2px dashed var(--gray-200)', borderRadius: 8, textAlign: 'center' }}>
                <ImageIcon size={32} color="var(--gray-500)" style={{ marginBottom: 8 }} />
                <div style={{ fontSize: 14, marginBottom: 12 }}>Pick an image from the Media Library</div>
                <button onClick={() => setPickerOpen(true)} style={{ ...primaryBtn, padding: '8px 18px' }}>Browse Media Library</button>
              </div>
            )}
          </Card>

          <Card title="Push to Meta">
            <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 10 }}>
              Creates a <strong>PAUSED</strong> campaign with one ad set and one{' '}
              {isCarousel ? <>carousel ad of <strong>{cards.length}</strong> card{cards.length === 1 ? '' : 's'}</> : 'ad'}. Go to Ads Manager to review targeting, add placements, and turn it live.
            </div>
            {isCarousel && cards.length < 2 ? (
              <div style={{ marginBottom: 10, padding: '8px 10px', background: '#fff8ef', border: '1px solid #ffd9a8', borderRadius: 4, fontSize: 11.5, color: '#9a5b00', lineHeight: 1.45 }}>
                A carousel needs at least <strong>2</strong> images. Add {2 - cards.length} more, or switch back to a single image.
              </div>
            ) : null}
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={push} disabled={!canPush} style={{ ...primaryBtn, flex: 1, padding: '12px', fontSize: 15, opacity: canPush ? 1 : 0.5, cursor: canPush ? 'pointer' : 'not-allowed' }}>
                {pushing ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite', verticalAlign: 'middle', marginRight: 6 }} /> Pushing…</> : <><Send size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} /> Push to Meta (paused)</>}
              </button>
              <button onClick={() => saveDraft()} disabled={draftSaving}
                style={{ ...secondaryBtn, padding: '12px 14px', fontSize: 13, opacity: draftSaving ? 0.5 : 1 }}
                title={currentDraft ? `Save changes to the shared draft "${currentDraft.name}"` : 'Save as a shared draft your team can pick up before launch'}>
                {draftSaving ? 'Saving…' : currentDraft ? 'Save draft' : 'Save as draft'}
              </button>
              <button onClick={previewCurrentForm} disabled={isCarousel ? cards.length < 2 : !selectedAsset}
                style={{ ...secondaryBtn, padding: '12px 14px', fontSize: 13, opacity: (isCarousel ? cards.length < 2 : !selectedAsset) ? 0.5 : 1, cursor: (isCarousel ? cards.length < 2 : !selectedAsset) ? 'not-allowed' : 'pointer' }}
                title="See what this ad will look like — screenshot-ready.">
                Preview
              </button>
            </div>
            {pushError ? (
              <div style={{ marginTop: 10, padding: 10, background: '#fff5f5', border: '1px solid #ffd6d6', borderRadius: 6, fontSize: 12, color: '#c33', whiteSpace: 'pre-wrap' }}>
                <AlertCircle size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} /> {pushError}
              </div>
            ) : null}
            {pushResult ? <PushResultBox result={pushResult} onPreview={pushResult.pushId ? () => openPastCampaign(pushResult.pushId) : null} previewing={openingPast === pushResult.pushId} /> : null}
          </Card>
        </div>
      </div>

      {pickerOpen ? (
        <MediaPicker
          onClose={() => setPickerOpen(false)}
          onPick={(asset) => { setSelectedAsset(asset); setCropOffset({ x: 0.5, y: 0.5 }); setPickerOpen(false); }}
        />
      ) : null}

      {cardPickerOpen ? (
        <MediaPicker
          multiSelect
          onClose={() => setCardPickerOpen(false)}
          onPick={(assets) => addCards(Array.isArray(assets) ? assets : [assets])}
        />
      ) : null}

      {previewData ? (
        <CampaignPreviewModal data={previewData} onClose={() => setPreviewData(null)} />
      ) : null}
      </>)}

      {/* Campaign reporting moved to the Performance tab (2026-07-15, Greg's ask:
          paid + organic performance on ONE page). Leave a pointer so muscle
          memory that scrolled down here still finds it in one click. */}
      {view === 'paid' && platform !== 'tiktok' && (
        <div style={{ marginTop: 26, borderTop: '2px solid var(--gray-100)', paddingTop: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 12.5, color: 'var(--gray-500)' }}>Campaign reporting now lives on the <strong>Performance</strong> tab, together with organic post performance.</span>
          <button onClick={() => setPlatform('performance')}
            style={{ padding: '6px 14px', fontSize: 12, fontWeight: 700, fontFamily: 'Montserrat', borderRadius: 16, cursor: 'pointer', border: '1px solid var(--navy)', background: 'var(--navy)', color: '#fff' }}>
            Open Performance →
          </button>
        </div>
      )}
    </div>
  );
}

// ── Which shape to pick ──────────────────────────────────────────────────────
// Greg asked for this note, on the assumption that landscape is the strong choice.
// It is the opposite: landscape is the weakest shape on a phone, because it fills
// the least screen height in a feed people are scrolling vertically.
//
// ⚠️ DO NOT "improve" this by quoting our own ROAS per ratio. It was measured
// (89 ads, $78,564 lifetime, 2026-09-10) and it is CONFOUNDED, badly: the
// landscape bucket reads ROAS 197 only because one 2023 Advantage+ catalog
// campaign in it returned 524 against warm retargeting, and most of the rest is
// "VIP Dec 2024" / "Xmas Full Email List" — our own mailing list. That measures
// audience temperature, not creative shape. The guidance below is mechanical
// (how much screen a shape occupies, what Meta crops) rather than statistical,
// which is the only claim the data actually supports.
function ShapeGuidance({ isCarousel, activePlacement }) {
  const wrap = {
    marginBottom: 12, padding: '9px 11px', borderRadius: 6, fontSize: 11.5, lineHeight: 1.5,
    background: '#f2f7fa', border: '1px solid #d8e6ef', color: '#33505f',
  };
  if (isCarousel) {
    return (
      <div style={wrap}>
        <strong>Every card is square, and that is not a choice.</strong> Meta renders all the cards
        in a carousel at one shape and pads anything that does not match, so a mixed set only looks
        wrong once the ad is live. Crop each card to fill the square. Use a carousel when you have
        several things to show (products, sizes, steps) — otherwise one strong image usually beats
        five average ones.
      </div>
    );
  }
  const p = PLACEMENT_BY_KEY[activePlacement];
  return (
    <div style={wrap}>
      <strong>Taller works harder.</strong> Almost everyone sees these on a phone, scrolling
      vertically, so the shape that fills more screen gets more attention. <strong>Feed 4:5</strong> is
      the strongest default. Pick <strong>Feed 1:1</strong> when the ad will run across every
      placement. <strong>Landscape 1.91:1 is the weakest on mobile</strong> — it is really a desktop
      and right-column shape.
      {p?.weak ? (
        <div style={{ marginTop: 7, paddingTop: 7, borderTop: '1px solid #cfe0ea', color: '#9a5b00' }}>
          <AlertCircle size={12} style={{ verticalAlign: 'middle', marginRight: 5 }} />
          You have landscape selected. Unless this is aimed at desktop, <strong>Feed 4:5</strong> or{' '}
          <strong>Feed 1:1</strong> will do better.
        </div>
      ) : null}
      {/* This has to describe what the builder ACTUALLY does. The shape is not
          only a crop here — each one carries its own publisher_platforms /
          positions, so choosing it also chooses where the ad is allowed to run.
          Telling people to "push it twice" would be worse advice than none: two
          campaigns at these budgets split the spend and the learning. */}
      <div style={{ marginTop: 7, paddingTop: 7, borderTop: '1px solid #cfe0ea' }}>
        The shape also decides <strong>where this ad runs</strong>: 4:5 and 1:1 go to the feed,
        9:16 goes to Stories and Reels, 1.91:1 goes to desktop and the right column. So pick the
        shape for the place you actually want it — not the one your photo happens to be.
      </div>
    </div>
  );
}

// ── Carousel editor ──────────────────────────────────────────────────────────
// A carousel is 2-10 cards the viewer swipes through. Each card is its own image,
// its own headline and its own link — so a carousel can be five products going to
// five product pages, which a single-image ad cannot do.
//
// ⚠️ Every card is cropped to the SAME square. Meta renders carousel cards square
// and letterboxes anything else instead of rejecting it, so a mixed-ratio set
// looks broken only once it is live.
function CarouselEditor({ cards, maxCards, onAdd, onUpdate, onRemove, onMove, optimize, setOptimize, endCard, setEndCard }) {
  const square = PLACEMENT_BY_KEY.square;
  const full = cards.length >= maxCards;

  return (
    <>
      {cards.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
          {cards.map((c, i) => (
            <div key={`${c.asset.id}-${i}`} style={{ display: 'flex', gap: 10, padding: 10, border: '1px solid var(--gray-200)', borderRadius: 6, background: '#fafafa' }}>
              <div style={{ flexShrink: 0 }}>
                <CropPreview asset={c.asset} placement={square} offset={c.offset} setOffset={(o) => onUpdate(i, { offset: o })} height={132} />
                <div style={{ textAlign: 'center', fontSize: 10, color: 'var(--gray-500)', marginTop: 4 }}>Card {i + 1} · drag to crop</div>
              </div>

              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                <input style={{ ...inputStyle, fontSize: 12 }} placeholder="Card headline (optional)"
                  value={c.headline} onChange={(e) => onUpdate(i, { headline: e.target.value })} />
                <input style={{ ...inputStyle, fontSize: 12 }} placeholder="Card description (optional)"
                  value={c.description} onChange={(e) => onUpdate(i, { description: e.target.value })} />
                <input style={{ ...inputStyle, fontSize: 12 }} placeholder="Card link — blank uses the ad's destination"
                  value={c.landingUrl} onChange={(e) => onUpdate(i, { landingUrl: e.target.value })} />
                {c.asset.media_kind === 'video' ? (
                  <div style={{ fontSize: 10.5, color: '#9a5b00' }}>Video card runs as its poster frame.</div>
                ) : null}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
                <button onClick={() => onMove(i, -1)} disabled={i === 0} style={cardIconBtn(i === 0)} title="Move up">↑</button>
                <button onClick={() => onMove(i, 1)} disabled={i === cards.length - 1} style={cardIconBtn(i === cards.length - 1)} title="Move down">↓</button>
                <button onClick={() => onRemove(i)} style={{ ...cardIconBtn(false), color: '#c33', borderColor: '#f0c9c9' }} title="Remove card">✕</button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ padding: '32px 20px', border: '2px dashed var(--gray-200)', borderRadius: 8, textAlign: 'center', marginBottom: 12 }}>
          <ImageIcon size={30} color="var(--gray-500)" style={{ marginBottom: 8 }} />
          <div style={{ fontSize: 13.5, marginBottom: 4 }}>Pick 2 to {maxCards} images for the carousel</div>
          <div style={{ fontSize: 11.5, color: 'var(--gray-500)', marginBottom: 12 }}>Hold and tick several at once in the picker.</div>
          <button onClick={onAdd} style={{ ...primaryBtn, padding: '8px 18px' }}>Browse Media Library</button>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>
          {cards.length} of {maxCards} card{cards.length === 1 ? '' : 's'}
          {full ? ' · the most Meta allows' : ''}
        </div>
        {cards.length ? (
          <button onClick={onAdd} disabled={full} style={{ ...secondaryBtn, opacity: full ? 0.5 : 1, cursor: full ? 'not-allowed' : 'pointer' }}>Add images</button>
        ) : null}
      </div>

      <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--gray-100)', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12, cursor: 'pointer' }}>
          <input type="checkbox" checked={optimize} onChange={(e) => setOptimize(e.target.checked)} style={{ marginTop: 2 }} />
          <span>
            <strong>Let Meta reorder the cards</strong> by which performs best.
            <span style={{ color: 'var(--gray-500)' }}> Turn this off if the cards tell a story in order.</span>
          </span>
        </label>
        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12, cursor: 'pointer' }}>
          <input type="checkbox" checked={endCard} onChange={(e) => setEndCard(e.target.checked)} style={{ marginTop: 2 }} />
          <span>
            <strong>Add the end card</strong> with our Page profile after the last image.
          </span>
        </label>
      </div>
    </>
  );
}

function cardIconBtn(disabled) {
  return {
    width: 28, height: 26, fontSize: 12, lineHeight: 1, borderRadius: 4, cursor: disabled ? 'default' : 'pointer',
    border: '1px solid var(--gray-200)', background: '#fff', color: 'var(--navy)', opacity: disabled ? 0.35 : 1,
  };
}

// ── Crop preview ─────────────────────────────────────────────────────────────
function CropPreview({ asset, placement, offset, setOffset, height = 360 }) {
  const ref = useRef(null);
  const [dragging, setDragging] = useState(false);

  const onPointerDown = (e) => { setDragging(true); e.currentTarget.setPointerCapture(e.pointerId); move(e); };
  const onPointerMove = (e) => { if (dragging) move(e); };
  const onPointerUp = (e) => { setDragging(false); try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {} };
  const move = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const y = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
    setOffset({ x, y });
  };

  // For a VIDEO the "original" is an MP4 that r2-image can't rasterize (renders as
  // a broken/black frame), so use its poster (r2_thumb_key). Images use the original.
  const imgKey = asset.media_kind === 'video'
    ? (asset.r2_thumb_key || asset.r2_original_key)
    : (asset.r2_original_key || asset.r2_thumb_key);
  const url = `/.netlify/functions/r2-image?key=${encodeURIComponent(imgKey)}`;

  // Preview frame height is caller-set (carousel cards use a smaller one). Its
  // width follows the target ratio.
  const frameH = height;
  const frameW = Math.round(frameH * placement.ratio);

  return (
    <div style={{ display: 'flex', justifyContent: 'center', background: '#1d1d1f', padding: 12, borderRadius: 6 }}>
      <div
        ref={ref}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        style={{
          position: 'relative', width: frameW, height: frameH,
          background: '#000', overflow: 'hidden', cursor: dragging ? 'grabbing' : 'grab',
          border: '1px solid rgba(255,255,255,0.2)',
        }}
      >
        <img
          src={url}
          alt=""
          draggable={false}
          style={{
            position: 'absolute', width: '100%', height: '100%',
            objectFit: 'cover', objectPosition: `${offset.x * 100}% ${offset.y * 100}%`,
            pointerEvents: 'none', userSelect: 'none',
          }}
        />
        <div style={{ position: 'absolute', bottom: 6, right: 8, color: '#fff', fontSize: 10, fontFamily: 'monospace', background: 'rgba(0,0,0,0.65)', padding: '2px 6px', borderRadius: 3 }}>
          {placement.dims}
        </div>
      </div>
    </div>
  );
}

// Render the currently-configured crop to a base64 JPEG at the target placement's
// native resolution. Matches what CropPreview displays pixel-for-pixel because
// both use `objectFit: cover` semantics.
async function renderCroppedJpegBase64(asset, ratio, offset) {
  // For a VIDEO the "original" is an MP4 that r2-image can't rasterize (renders as
  // a broken/black frame), so use its poster (r2_thumb_key). Images use the original.
  const imgKey = asset.media_kind === 'video'
    ? (asset.r2_thumb_key || asset.r2_original_key)
    : (asset.r2_original_key || asset.r2_thumb_key);
  const url = `/.netlify/functions/r2-image?key=${encodeURIComponent(imgKey)}`;

  // Fetch as blob → load into Image so canvas can be tainted-free (same-origin).
  const blob = await fetch(url).then((r) => r.blob());
  const objectUrl = URL.createObjectURL(blob);
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('Could not load image from Media Library'));
      el.src = objectUrl;
    });

    // Canvas dims: 800 px wide keeps file sizes under Netlify's 6 MB body limit
    // when pushing multi-variant proposals (1080 px at Q0.9 could exceed it).
    // 800 px is above Meta's 600 px minimum and looks correct in Ads Manager.
    let canvasW, canvasH;
    if (Math.abs(ratio - 1) < 0.01)          { canvasW = 800;  canvasH = 800; }
    else if (Math.abs(ratio - 0.8) < 0.01)   { canvasW = 800;  canvasH = 1000; }
    else if (Math.abs(ratio - 0.5625) < 0.01){ canvasW = 800;  canvasH = 1422; }
    else if (Math.abs(ratio - 1.91) < 0.02)  { canvasW = 1000; canvasH = 524; }
    else { canvasW = 800; canvasH = Math.round(800 / ratio); }

    const canvas = document.createElement('canvas');
    canvas.width = canvasW; canvas.height = canvasH;
    const ctx = canvas.getContext('2d');

    // Mirror CSS `object-fit: cover`: scale the image so it fully covers the
    // target, then shift by (offset.x, offset.y) within the overflow area.
    const imgRatio = img.naturalWidth / img.naturalHeight;
    const targetRatio = canvasW / canvasH;
    let drawW, drawH;
    if (imgRatio > targetRatio) {
      // image is wider than target — height fills
      drawH = canvasH;
      drawW = drawH * imgRatio;
    } else {
      drawW = canvasW;
      drawH = drawW / imgRatio;
    }
    const dx = -(drawW - canvasW) * offset.x;
    const dy = -(drawH - canvasH) * offset.y;
    ctx.drawImage(img, dx, dy, drawW, drawH);
    const jpeg = canvas.toDataURL('image/jpeg', 0.8);
    return jpeg.split(',')[1];
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

// ── Push result box ──────────────────────────────────────────────────────────
function PushResultBox({ result, onPreview, previewing }) {
  const adSets = result.adSets || [];
  const ads = result.ads || [];
  return (
    <div style={{ marginTop: 10, padding: 12, background: '#f0f9f4', border: '1px solid #b7e4c7', borderRadius: 6, fontSize: 12 }}>
      <div style={{ fontWeight: 600, color: '#1a7f37', marginBottom: 8 }}>
        <CheckCircle2 size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
        Pushed {ads.length} ad{ads.length === 1 ? '' : 's'} across {adSets.length} ad set{adSets.length === 1 ? '' : 's'} (all paused)
      </div>
      <div style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--gray-500)', lineHeight: 1.6 }}>
        campaign: {result.campaignId}<br />
        {adSets.map((a) => <span key={a.id}>ad set "{a.name}": {a.id}<br /></span>)}
        {ads.map((a) => <span key={a.id}>ad "{a.name}": {a.id}<br /></span>)}
      </div>
      <div style={{ marginTop: 8, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        {result.adsManagerUrl ? (
          <a href={result.adsManagerUrl} target="_blank" rel="noreferrer"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--orange)', textDecoration: 'none', fontWeight: 600 }}>
            Open in Ads Manager <ExternalLink size={12} />
          </a>
        ) : null}
        {onPreview ? (
          <button onClick={onPreview} disabled={previewing}
            style={{ background: '#fff', color: 'var(--navy)', border: '1px solid var(--navy)', borderRadius: 4, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: previewing ? 'wait' : 'pointer' }}>
            {previewing ? <><Loader2 size={11} style={{ animation: 'spin 1s linear infinite', verticalAlign: 'middle', marginRight: 5 }} /> Loading…</> : 'Preview for screenshot'}
          </button>
        ) : null}
      </div>
    </div>
  );
}

// ── AI suggestion panel ──────────────────────────────────────────────────────
function AISuggestPanel({ prompt, setPrompt, brand, setBrand, format, setFormat, landingUrl, setLandingUrl, landing, landingLoading, landingWarning, onReadLanding, loading, error, bailText, followUp, setFollowUp, onFollowUp, proposal, seenAssets, onSuggest, onApply, onClear, onSwap, onCycle, onPushAll, onPreview, pushing }) {
  const showCloseButton = proposal || bailText || error;
  const [activePreset, setActivePreset] = useState(null);
  return (
    <div style={{ background: 'linear-gradient(135deg, #fff 0%, #fff8f4 100%)', border: '1px solid #ffd6c0', borderRadius: 8, padding: 16, marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <Wand2 size={16} color="var(--orange)" />
        <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 14 }}>Let Claude propose a campaign</div>
        <div style={{ fontSize: 11, color: 'var(--gray-500)', flex: 1 }}>
          {format === 'carousel'
            ? 'Describe the theme and Claude will browse the Media Library + draft 2-4 carousels of 3-6 cards.'
            : 'Describe the theme and Claude will browse the Media Library + draft 4-10 variants.'}
        </div>
        {showCloseButton ? (
          <button onClick={onClear} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: '1px solid var(--gray-200)', padding: '4px 10px', fontSize: 11, borderRadius: 4, cursor: 'pointer', color: 'var(--gray-500)' }}>
            <RefreshCw size={11} /> Clear
          </button>
        ) : null}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8, alignItems: 'center' }}>
        <span style={{ fontSize: 10, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Brand:</span>
        {[
          { k: null,             label: 'Both' },
          { k: 'crab-place',     label: 'Crab Place' },
          { k: 'crab-and-cruise',label: 'Crab & Cruise' },
        ].map((b) => (
          <button key={b.label} type="button" onClick={() => setBrand(b.k)}
            style={{
              padding: '4px 10px', fontSize: 11, fontWeight: 600,
              background: brand === b.k ? 'var(--navy)' : '#fff',
              color: brand === b.k ? '#fff' : 'var(--navy)',
              border: `1px solid ${brand === b.k ? 'var(--navy)' : 'var(--gray-200)'}`,
              borderRadius: 999, cursor: 'pointer',
            }}>
            {b.label}
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8, alignItems: 'center' }}>
        <span style={{ fontSize: 10, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Format:</span>
        {[
          { k: 'single',   label: 'Single Image' },
          { k: 'carousel', label: 'Carousel' },
        ].map((f) => (
          <button key={f.k} type="button" onClick={() => setFormat(f.k)} disabled={loading}
            style={{
              padding: '4px 10px', fontSize: 11, fontWeight: 600,
              background: format === f.k ? 'var(--navy)' : '#fff',
              color: format === f.k ? '#fff' : 'var(--navy)',
              border: `1px solid ${format === f.k ? 'var(--navy)' : 'var(--gray-200)'}`,
              borderRadius: 999, cursor: loading ? 'not-allowed' : 'pointer',
            }}>
            {f.label}
          </button>
        ))}
        {proposal && (proposal.format || 'single') !== format ? (
          <span style={{ fontSize: 11, color: 'var(--orange)' }}>Click Suggest to redo this as {format === 'carousel' ? 'carousels' : 'single images'}.</span>
        ) : null}
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 10, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>Landing page:</span>
        <div style={{ flex: '1 1 420px', display: 'flex', alignItems: 'center', gap: 6, background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 6, padding: '0 8px' }}>
          <Link2 size={13} color="var(--gray-500)" />
          <input type="url" value={landingUrl} disabled={loading}
            onChange={(e) => setLandingUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && landingUrl.trim()) { e.preventDefault(); onReadLanding(); } }}
            placeholder="Paste the page the ad sends people to, e.g. https://www.crabplace.com/free-crab-balls/"
            style={{ flex: 1, border: 'none', outline: 'none', padding: '7px 0', fontSize: 12, fontFamily: 'inherit', background: 'transparent' }} />
        </div>
        <button type="button" onClick={onReadLanding} disabled={!landingUrl.trim() || loading || landingLoading}
          style={{ padding: '5px 12px', fontSize: 11, fontWeight: 600, background: '#fff', color: 'var(--navy)', border: '1px solid var(--navy)', borderRadius: 999, cursor: (!landingUrl.trim() || loading || landingLoading) ? 'not-allowed' : 'pointer', opacity: (!landingUrl.trim() || loading || landingLoading) ? 0.5 : 1, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {landingLoading ? <><Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} /> Reading…</> : 'Read Page'}
        </button>
      </div>
      {landing ? (
        <div style={{ marginBottom: 8, padding: '8px 12px', background: '#f0f9f4', border: '1px solid #b7e4c7', borderRadius: 6, fontSize: 12, color: '#1a7f37', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <CheckCircle2 size={14} style={{ flexShrink: 0, marginTop: 1 }} />
          <div>
            <strong>Read {landing.title || landing.url}.</strong> Claude will write the ads from this page's copy and offer
            {(landing.assets || []).length ? <>, starting with the {landing.assets.length} Media Library photo{landing.assets.length === 1 ? '' : 's'} on it</> : ''}.
            Every ad and the Destination below now point to <span style={{ wordBreak: 'break-all' }}>{landing.url}</span>.
            The box below is optional: add anything the page does not say (audience, budget, angle).
          </div>
        </div>
      ) : null}
      {landingWarning && !landing ? (
        <div style={{ marginBottom: 8, padding: '8px 12px', background: '#fff8e1', border: '1px solid #f0c36d', borderRadius: 6, fontSize: 12, color: '#7a5200', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
          <div>
            <strong>Could not read that page, so Claude will use your description instead.</strong>{' '}
            {landingWarning}{' '}
            Every ad still points at the link above. Crab &amp; Cruise pages build themselves in the
            browser, so there is no text for our reader to see yet.
          </div>
        </div>
      ) : null}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
        <span style={{ fontSize: 10, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600, alignSelf: 'center' }}>Presets:</span>
        {PROMPT_PRESETS.map((p) => {
          const active = activePreset === p.label;
          return (
            <button key={p.label} type="button"
              onClick={() => { if (p.append) { setPrompt((prev) => (prev && prev.trim() ? prev.trimEnd() + '\n\n' : '') + p.text); } else { setPrompt(p.text); } setActivePreset(p.label); }}
              style={{
                padding: '4px 10px', fontSize: 11, fontWeight: 600,
                background: active ? 'var(--navy)' : '#fff',
                color: active ? '#fff' : 'var(--navy)',
                border: `1px solid ${active ? 'var(--navy)' : 'var(--gray-200)'}`,
                borderRadius: 999, cursor: 'pointer',
              }}>
              {p.label}
            </button>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <textarea
          value={prompt}
          onChange={(e) => { setPrompt(e.target.value); setActivePreset(null); }}
          placeholder={landingUrl.trim() ? 'Optional. Anything the page does not say, e.g. target past customers in the Mid-Atlantic, $50/day' : "e.g. Father's Day push. Steamed Maryland crabs, family gatherings, dads 25 to 55, $50/day"}
          rows={2}
          disabled={loading}
          style={{ flex: 1, padding: '10px 12px', fontSize: 13, border: '1px solid var(--gray-200)', borderRadius: 6, fontFamily: 'inherit', resize: 'vertical' }}
        />
        <MicButton
          title="Dictate your campaign brief"
          style={{ alignSelf: 'center' }}
          onTranscript={(text) => { setPrompt((p) => (p ? p + ' ' : '') + text); setActivePreset(null); }}
        />
        <button onClick={onSuggest} disabled={(!prompt.trim() && !landingUrl.trim()) || loading}
          style={{ background: 'var(--orange)', color: '#fff', border: 'none', borderRadius: 6, padding: '0 20px', fontSize: 13, fontWeight: 600, cursor: ((!prompt.trim() && !landingUrl.trim()) || loading) ? 'not-allowed' : 'pointer', opacity: ((!prompt.trim() && !landingUrl.trim()) || loading) ? 0.5 : 1, display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 140, justifyContent: 'center' }}>
          {loading ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Thinking…</> : <><Wand2 size={14} /> Suggest</>}
        </button>
      </div>
      {error ? (
        <div style={{ marginTop: 10, padding: 12, background: '#fff5f5', border: '1px solid #ffd6d6', borderRadius: 6, fontSize: 12, color: '#7a1f1f', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
            <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1, color: '#c33' }} />
            <span>{error}</span>
          </div>
        </div>
      ) : null}
      {bailText ? (
        <div style={{ marginTop: 10 }}>
          <div style={{ padding: 14, background: '#fff8e6', border: '1px solid #ffe9a8', borderRadius: 6, fontSize: 13, color: '#5a4400', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <span style={{ fontSize: 16 }}>🦀</span>
              <div style={{ flex: 1 }}>{bailText}</div>
            </div>
          </div>
          <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <textarea
              value={followUp}
              onChange={(e) => setFollowUp(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onFollowUp(); } }}
              placeholder={"Reply to Claude — e.g. \"Go with Option A\" or \"Try searches for people toasting with crabs\""}
              rows={2}
              disabled={loading}
              style={{ flex: 1, padding: '8px 10px', fontSize: 13, border: '1px solid var(--gray-200)', borderRadius: 6, fontFamily: 'inherit', resize: 'vertical' }}
            />
            <button onClick={onFollowUp} disabled={!followUp.trim() || loading}
              style={{ background: 'var(--orange)', color: '#fff', border: 'none', borderRadius: 6, padding: '0 16px', fontSize: 13, fontWeight: 600, cursor: (!followUp.trim() || loading) ? 'not-allowed' : 'pointer', opacity: (!followUp.trim() || loading) ? 0.5 : 1, minHeight: 40, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {loading ? <><Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> Thinking…</> : <><Send size={13} /> Send</>}
            </button>
          </div>
        </div>
      ) : null}
      {proposal ? (
        <ProposalView proposal={proposal} seenAssets={seenAssets}
          onApply={onApply} onSwap={onSwap} onCycle={onCycle} onPushAll={onPushAll} onPreview={onPreview} pushing={pushing} />
      ) : null}
    </div>
  );
}

function ProposalView({ proposal, seenAssets, onApply, onSwap, onCycle, onPushAll, onPreview, pushing }) {
  const isCarouselProposal = proposal.format === 'carousel';
  const resolvable = isCarouselProposal
    ? (proposal.variants || []).filter((v) => (v.cards || []).filter((c) => seenAssets.get(c.mediaAssetId)).length >= 2).length
    : (proposal.variants || []).filter((v) => seenAssets.get(v.mediaAssetId)).length;
  const placementCount = isCarouselProposal ? 1 : new Set((proposal.variants || []).map((v) => v.placement || 'square')).size;
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 6, padding: 12, marginBottom: 10 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy)', marginBottom: 4 }}>{proposal.campaignName}</div>
        <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 6 }}>
          {proposal.objective.replace(/^OUTCOME_/, '')} · ${proposal.dailyBudgetUsd}/day
          {proposal.audience ? (
            <>
              {' · '}
              {(proposal.audience.countries || []).join(',')} · ages {proposal.audience.ageMin || '?'}-{proposal.audience.ageMax || '?'}
              {proposal.audience.gender && proposal.audience.gender !== 'all' ? ` · ${proposal.audience.gender}` : ''}
            </>
          ) : null}
        </div>
        {proposal.rationale ? <div style={{ fontSize: 12, color: '#1d1d1f', fontStyle: 'italic' }}>{proposal.rationale}</div> : null}
        <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 6, wordBreak: 'break-all' }}>→ {proposal.landingUrl}</div>
        <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button onClick={onPushAll} disabled={pushing || resolvable === 0}
            style={{ background: 'var(--orange)', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 18px', fontSize: 13, fontWeight: 600, cursor: (pushing || resolvable === 0) ? 'not-allowed' : 'pointer', opacity: (pushing || resolvable === 0) ? 0.5 : 1, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {pushing ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Pushing {resolvable} ads…</> : <><Send size={14} /> Push all {resolvable} {isCarouselProposal ? 'carousels' : 'variants'} (paused)</>}
          </button>
          {onPreview ? (
            <button onClick={onPreview} disabled={resolvable === 0}
              style={{ background: '#fff', color: 'var(--navy)', border: '1px solid var(--navy)', borderRadius: 6, padding: '10px 16px', fontSize: 13, fontWeight: 600, cursor: resolvable === 0 ? 'not-allowed' : 'pointer', opacity: resolvable === 0 ? 0.5 : 1 }}
              title="See what these ads will look like — screenshot-ready.">
              Preview
            </button>
          ) : null}
          {isCarouselProposal ? (
          <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>
            Creates 1 campaign with 1 square ad set and {resolvable} carousel ad{resolvable === 1 ? '' : 's'}, every card cropped square. All PAUSED.
          </div>
          ) : (
          <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>
            Creates 1 campaign with {placementCount} ad set{placementCount === 1 ? '' : 's'} (one per placement) and {resolvable} ad{resolvable === 1 ? '' : 's'}, each cropped to its native ratio. All PAUSED.
          </div>
          )}
        </div>
      </div>
      <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 8 }}>
        Not the right photo? Use the arrows to flip through the other photos Claude found, or Swap to pick any image from the Media Library.
      </div>
      {isCarouselProposal ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {(proposal.variants || []).map((v, i) => (
            <CarouselVariantCard key={i} variant={v} seenAssets={seenAssets}
              onApply={() => onApply(v)}
              onSwap={(card) => onSwap({ variant: i, card })}
              onCycle={(card, dir) => onCycle({ variant: i, card }, dir)} />
          ))}
        </div>
      ) : (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 10 }}>
        {(proposal.variants || []).map((v, i) => (
          <VariantCard key={i} variant={v} asset={seenAssets.get(v.mediaAssetId)}
            onApply={() => onApply(v)}
            onSwap={() => onSwap({ variant: i, card: null })}
            onCycle={(dir) => onCycle({ variant: i, card: null }, dir)} />
        ))}
      </div>
      )}
    </div>
  );
}

// The swap controls under every proposed image: previous / Swap / next.
function SwapBar({ onSwap, onCycle, compact = false }) {
  const btn = {
    background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 4, cursor: 'pointer',
    color: 'var(--navy)', fontWeight: 600, fontSize: compact ? 10 : 11, padding: compact ? '3px 6px' : '4px 8px',
    display: 'inline-flex', alignItems: 'center', gap: 4, justifyContent: 'center',
  };
  return (
    <div style={{ display: 'flex', gap: 4, padding: compact ? '4px 0 0' : '6px 8px', background: compact ? 'transparent' : 'var(--gray-50, #fafafa)', borderBottom: compact ? 'none' : '1px solid var(--gray-200)' }}>
      <button type="button" style={btn} onClick={() => onCycle(-1)} title="Previous photo Claude found"><ChevronLeft size={compact ? 12 : 14} /></button>
      <button type="button" style={{ ...btn, flex: 1 }} onClick={onSwap} title="Pick any image from the Media Library">
        <RefreshCw size={compact ? 10 : 11} /> {compact ? 'Swap' : 'Swap Image'}
      </button>
      <button type="button" style={btn} onClick={() => onCycle(1)} title="Next photo Claude found"><ChevronRight size={compact ? 12 : 14} /></button>
    </div>
  );
}

function CarouselVariantCard({ variant, seenAssets, onApply, onSwap, onCycle }) {
  const cards = variant.cards || [];
  const usable = cards.filter((c) => seenAssets.get(c.mediaAssetId)).length;
  return (
    <div style={{ background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 6, padding: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <div style={{ background: 'var(--navy)', color: '#fff', padding: '2px 6px', fontSize: 10, borderRadius: 3, textTransform: 'uppercase', letterSpacing: 0.3 }}>Carousel · {cards.length} cards</div>
        <div style={{ fontWeight: 600, color: 'var(--navy)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{variant.adName}</div>
        <div style={{ background: 'var(--orange)', color: '#fff', padding: '2px 6px', fontSize: 10, borderRadius: 3, fontWeight: 600 }}>{(variant.ctaType || '').replace(/_/g, ' ')}</div>
      </div>
      <div style={{ color: 'var(--gray-500)', fontSize: 12, lineHeight: 1.4, marginBottom: 8 }}>{variant.primaryText}</div>
      <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
        {cards.map((c, j) => {
          const asset = seenAssets.get(c.mediaAssetId);
          return (
            <div key={j} style={{ flex: '0 0 170px', border: '1px solid var(--gray-200)', borderRadius: 4, overflow: 'hidden', background: '#fff' }}>
              <div style={{ aspectRatio: '1 / 1', background: asset ? '#000' : '#fff5f5', position: 'relative', cursor: 'pointer' }}
                onClick={() => onSwap(j)} title="Pick a different image">
                {asset ? (
                  <img src={`/.netlify/functions/r2-image?key=${encodeURIComponent(asset.r2_thumb_key)}`} alt={asset.alt_text || ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#c33', fontSize: 11, padding: 8, textAlign: 'center' }}>Image {c.mediaAssetId} not found. Click to pick one.</div>
                )}
                <div style={{ position: 'absolute', top: 4, left: 4, background: 'rgba(0,0,0,0.7)', color: '#fff', fontSize: 10, padding: '2px 6px', borderRadius: 3, fontWeight: 700 }}>{j + 1}</div>
                {asset?.human_starred ? (
                  <div style={{ position: 'absolute', top: 4, right: 4, background: '#ffd60a', color: '#1d1d1f', fontSize: 10, padding: '2px 5px', borderRadius: 3, fontWeight: 700 }}>★</div>
                ) : null}
              </div>
              <div style={{ padding: '4px 6px 6px' }}>
                <SwapBar compact onSwap={() => onSwap(j)} onCycle={(dir) => onCycle(j, dir)} />
                <div style={{ fontSize: 12, fontWeight: 600, color: '#1d1d1f', marginTop: 4, lineHeight: 1.3 }}>{c.headline}</div>
                {c.description ? <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{c.description}</div> : null}
              </div>
            </div>
          );
        })}
      </div>
      {variant.rationale ? <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 6, paddingTop: 6, borderTop: '1px dashed var(--gray-200)' }}>{variant.rationale}</div> : null}
      <button onClick={onApply} disabled={usable < 2}
        style={{ marginTop: 8, background: usable >= 2 ? 'var(--orange)' : '#ddd', color: '#fff', border: 'none', borderRadius: 4, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: usable >= 2 ? 'pointer' : 'not-allowed' }}>
        Use This Carousel
      </button>
    </div>
  );
}

function VariantCard({ variant, asset, onApply, onSwap, onCycle }) {
  const hasAsset = !!asset;
  return (
    <div style={{ background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 6, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {hasAsset ? (
        <div style={{ aspectRatio: '16 / 9', background: '#000', overflow: 'hidden', position: 'relative', cursor: 'pointer' }}
          onClick={onSwap} title="Click to swap this image">
          <img src={`/.netlify/functions/r2-image?key=${encodeURIComponent(asset.r2_thumb_key)}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          <div style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(0,0,0,0.7)', color: '#fff', fontSize: 10, padding: '3px 7px', borderRadius: 3, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <RefreshCw size={10} /> Swap
          </div>
          {asset.human_starred ? (
            <div style={{ position: 'absolute', top: 6, left: 6, background: '#ffd60a', color: '#1d1d1f', fontSize: 10, padding: '3px 7px', borderRadius: 3, fontWeight: 700 }}>★ Starred</div>
          ) : null}
        </div>
      ) : (
        <div style={{ aspectRatio: '16 / 9', background: '#fff5f5', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#c33', fontSize: 11, gap: 6, cursor: 'pointer' }}
          onClick={onSwap} title="Pick a replacement image">
          <div><AlertCircle size={14} style={{ marginRight: 6 }} /> Asset id {variant.mediaAssetId} not found</div>
          <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>click to pick a replacement</div>
        </div>
      )}
      <SwapBar onSwap={onSwap} onCycle={onCycle} />
      <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ background: 'var(--navy)', color: '#fff', padding: '2px 6px', fontSize: 10, borderRadius: 3, textTransform: 'uppercase', letterSpacing: 0.3 }}>{variant.placement || 'square'}</div>
          <div style={{ fontWeight: 600, color: 'var(--navy)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{variant.adName}</div>
        </div>
        <div style={{ color: '#1d1d1f', fontSize: 13, fontWeight: 600, lineHeight: 1.3 }}>{variant.headline}</div>
        <div style={{ color: 'var(--gray-500)', fontSize: 12, lineHeight: 1.4 }}>{variant.primaryText}</div>
        {variant.description ? <div style={{ color: 'var(--gray-500)', fontSize: 11, fontStyle: 'italic' }}>{variant.description}</div> : null}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ background: 'var(--orange)', color: '#fff', padding: '2px 6px', fontSize: 10, borderRadius: 3, fontWeight: 600 }}>{(variant.ctaType || '').replace(/_/g, ' ')}</div>
          {asset ? <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>· {asset.suggested_name}</div> : null}
        </div>
        {variant.rationale ? <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 4, paddingTop: 6, borderTop: '1px dashed var(--gray-200)' }}>{variant.rationale}</div> : null}
        <button onClick={onApply} disabled={!hasAsset}
          style={{ marginTop: 4, background: hasAsset ? 'var(--orange)' : '#ddd', color: '#fff', border: 'none', borderRadius: 4, padding: '6px 10px', fontSize: 12, fontWeight: 600, cursor: hasAsset ? 'pointer' : 'not-allowed' }}>
          Use This Variant
        </button>
      </div>
    </div>
  );
}

// ── Small presentational helpers ─────────────────────────────────────────────
// ── Targeting card: geo picker, audience include/exclude, audience manager ──
// Backed by netlify/functions/meta-audiences.js (list / geo-search /
// create-from-neon / create-lookalike / job-status). Geo chips + audience ids
// flow into the ad set's targeting spec via baseAudience() in MetaBuilder.
const AUD_FN = '/.netlify/functions/meta-audiences';

function TargetingCard({ geoChips, setGeoChips, incAud, setIncAud, excAud, setExcAud, countries, setCountries, ageMin, setAgeMin, ageMax, setAgeMax, gender, setGender }) {
  const [geoQ, setGeoQ] = useState('');
  const [geoResults, setGeoResults] = useState(null);
  const [geoBusy, setGeoBusy] = useState(false);
  const [presetBusy, setPresetBusy] = useState(false);
  const [audiences, setAudiences] = useState(null);
  const [audErr, setAudErr] = useState(null);
  const [audBusy, setAudBusy] = useState(false);
  const [mgrOpen, setMgrOpen] = useState(false);
  const [creating, setCreating] = useState(null);   // 'all_customers' | 'active_vips' | 'lookalike'
  const [job, setJob] = useState(null);             // { jobId, name, total, uploaded, status, error }
  const [lalOrigin, setLalOrigin] = useState('');
  const [lalRatio, setLalRatio] = useState('0.01');
  // Audiences belong to the ad account that created them, so the manager has to say which
  // account it is showing. Crab & Cruise audiences are built on the C&C account and are
  // NOT selectable from a Crab Place ad set (and vice versa).
  const [audAcct, setAudAcct] = useState('act_47263248');

  const call = async (payload) => {
    const res = await fetch(AUD_FN, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
    return data;
  };

  const loadAudiences = async (acct) => {
    setAudBusy(true); setAudErr(null);
    try { setAudiences((await call({ action: 'list', account: acct || audAcct })).audiences); }
    catch (e) { setAudErr(e.message); }
    finally { setAudBusy(false); }
  };
  useEffect(() => { loadAudiences(audAcct); }, [audAcct]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll the email-upload job until it finishes, then refresh the audience list.
  useEffect(() => {
    if (!job || job.status === 'done' || job.status === 'failed') return undefined;
    const t = setInterval(async () => {
      try {
        const j = await call({ action: 'job-status', jobId: job.jobId });
        setJob(j);
        if (j.status === 'done') loadAudiences();
      } catch (e) { /* transient — keep polling */ }
    }, 3000);
    return () => clearInterval(t);
  }, [job]); // eslint-disable-line react-hooks/exhaustive-deps

  const addChip = (r) => {
    setGeoChips((prev) => prev.some((c) => c.key === r.key && c.type === r.type)
      ? prev
      : [...prev, { key: r.key, name: r.name, type: r.type, ...(r.type === 'city' ? { radius: 25 } : {}) }]);
    setGeoResults(null); setGeoQ('');
  };

  const searchGeo = async () => {
    if (!geoQ.trim() || geoBusy) return;
    setGeoBusy(true); setAudErr(null);
    try { setGeoResults((await call({ action: 'geo-search', q: geoQ.trim() })).results); }
    catch (e) { setAudErr(e.message); }
    finally { setGeoBusy(false); }
  };

  // One-click preset: the next-day UPS Ground zone around Crisfield —
  // DE/MD/NJ/VA/DC whole states + Philadelphia and New York metros (25 mi).
  const applyGroundPreset = async () => {
    if (presetBusy) return;
    setPresetBusy(true); setAudErr(null);
    try {
      const wants = [
        { q: 'Delaware', types: ['region'] }, { q: 'Maryland', types: ['region'] },
        { q: 'New Jersey', types: ['region'] }, { q: 'Virginia', types: ['region'] },
        { q: 'District of Columbia', types: ['region'] },
        { q: 'Philadelphia', types: ['city'] }, { q: 'New York', types: ['city'] },
      ];
      const found = [];
      for (const w of wants) {
        const { results } = await call({ action: 'geo-search', q: w.q, types: w.types });
        const hit = (results || [])[0];
        if (hit) found.push({ key: hit.key, name: hit.name, type: hit.type, ...(hit.type === 'city' ? { radius: 25 } : {}) });
      }
      setGeoChips((prev) => {
        const next = [...prev];
        found.forEach((f) => { if (!next.some((c) => c.key === f.key && c.type === f.type)) next.push(f); });
        return next;
      });
    } catch (e) { setAudErr(e.message); }
    finally { setPresetBusy(false); }
  };

  const createFromNeon = async (source) => {
    if (creating) return;
    setCreating(source); setAudErr(null);
    try {
      const d = await call({ action: 'create-from-neon', source });
      // The server pins C&C sources to the C&C ad account; follow it so the new audience
      // is visible in the list below instead of appearing to have silently not been made.
      if (d.account && d.account !== audAcct) setAudAcct(d.account);
      setJob({ jobId: d.jobId, name: d.name, total: d.total, uploaded: 0, status: 'queued' });
    } catch (e) { setAudErr(e.message); }
    finally { setCreating(null); }
  };

  const createLookalike = async () => {
    if (creating || !lalOrigin) return;
    setCreating('lookalike'); setAudErr(null);
    try {
      const origin = (audiences || []).find((a) => a.id === lalOrigin);
      await call({ action: 'create-lookalike', originAudienceId: lalOrigin, originName: origin?.name, ratio: lalRatio, account: audAcct });
      await loadAudiences();
    } catch (e) { setAudErr(e.message); }
    finally { setCreating(null); }
  };

  const chipStyle = { display: 'inline-flex', alignItems: 'center', gap: 6, background: '#eef4f8', border: '1px solid #cfdfe9', borderRadius: 999, padding: '4px 10px', fontSize: 12.5, color: 'var(--navy)' };
  const xBtn = { background: 'none', border: 'none', cursor: 'pointer', color: '#8aa0af', fontWeight: 700, padding: 0, fontSize: 13, lineHeight: 1 };
  const miniBtn = { background: '#fff', border: '1px solid var(--navy)', color: 'var(--navy)', borderRadius: 6, padding: '6px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' };
  const typeLabel = { city: 'city', region: 'state', zip: 'zip', geo_market: 'DMA', country: 'country' };

  // One-click pills for the audiences CPx builds itself, so the win-back and exclusion
  // lists are reachable without hunting a dropdown of every audience on the ad account.
  // Ordered biggest-first; already-used ones drop out rather than render as dead pills.
  const quickPill = {
    ...chipStyle, cursor: 'pointer', background: '#fff',
    borderColor: 'var(--orange)', color: 'var(--orange)', fontWeight: 600,
  };

  const quickPicks = (chosen, setChosen, other) => {
    const picks = (audiences || [])
      .filter((a) => a.name && a.name.startsWith('CPx'))
      .filter((a) => !chosen.some((x) => x.id === a.id) && !other.some((x) => x.id === a.id))
      .sort((a, b) => (b.sizeLower || 0) - (a.sizeLower || 0));
    if (!picks.length) return null;
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
        {picks.map((a) => (
          <button
            key={a.id}
            type="button"
            style={quickPill}
            title={`Add ${a.name}${a.sizeLower ? ` (~${Number(a.sizeLower).toLocaleString()} people matched)` : ''}`}
            onClick={() => setChosen([...chosen, { id: a.id, name: a.name }])}
          >
            + {a.name.replace(/^CPx /, '')}
            {a.sizeLower ? <em style={{ fontStyle: 'normal', color: '#8aa0af' }}>~{Number(a.sizeLower).toLocaleString()}</em> : null}
          </button>
        ))}
      </div>
    );
  };

  const audPicker = (label, chosen, setChosen, other) => (
    <Field label={label}>
      {chosen.length ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
          {chosen.map((a) => (
            <span key={a.id} style={chipStyle}>
              {a.name}
              <button style={xBtn} onClick={() => setChosen(chosen.filter((x) => x.id !== a.id))} title="Remove">×</button>
            </span>
          ))}
        </div>
      ) : null}
      {quickPicks(chosen, setChosen, other)}
      <select
        value=""
        onChange={(e) => {
          const a = (audiences || []).find((x) => x.id === e.target.value);
          if (a && !chosen.some((x) => x.id === a.id)) setChosen([...chosen, { id: a.id, name: a.name }]);
        }}
        style={inputStyle}
      >
        <option value="">{audiences === null ? 'Loading audiences…' : '+ Add an audience…'}</option>
        {(audiences || []).filter((a) => !chosen.some((x) => x.id === a.id) && !other.some((x) => x.id === a.id)).map((a) => (
          <option key={a.id} value={a.id}>
            {a.name} {a.subtype === 'LOOKALIKE' ? '(lookalike)' : ''}{a.sizeLower ? ` — ~${Number(a.sizeLower).toLocaleString()}` : ''}
          </option>
        ))}
      </select>
    </Field>
  );

  return (
    <Card title="Targeting">
      <Field label="Locations">
        {geoChips.length ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
            {geoChips.map((g) => (
              <span key={`${g.type}:${g.key}`} style={chipStyle}>
                {g.name} <em style={{ fontStyle: 'normal', color: '#7d93a3' }}>({typeLabel[g.type] || g.type})</em>
                {g.type === 'city' ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                    +<input
                      type="number" min="10" max="50" value={g.radius}
                      onChange={(e) => setGeoChips(geoChips.map((c) => (c.key === g.key && c.type === g.type ? { ...c, radius: parseInt(e.target.value, 10) || 25 } : c)))}
                      style={{ width: 42, border: '1px solid #cfdfe9', borderRadius: 4, fontSize: 12, padding: '1px 3px' }}
                    />mi
                  </span>
                ) : null}
                <button style={xBtn} onClick={() => setGeoChips(geoChips.filter((c) => !(c.key === g.key && c.type === g.type)))} title="Remove">×</button>
              </span>
            ))}
          </div>
        ) : null}
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            type="text" value={geoQ} onChange={(e) => setGeoQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); searchGeo(); } }}
            placeholder="Search city, state, zip, or DMA…" style={{ ...inputStyle, flex: 1 }}
          />
          <button style={miniBtn} onClick={searchGeo} disabled={geoBusy}>{geoBusy ? '…' : 'Search'}</button>
        </div>
        {geoResults ? (
          <div style={{ border: '1px solid #dfe8ee', borderRadius: 6, marginTop: 6, maxHeight: 180, overflowY: 'auto' }}>
            {geoResults.length ? geoResults.map((r) => (
              <button key={`${r.type}:${r.key}`} onClick={() => addChip(r)}
                style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', borderBottom: '1px solid #f0f4f7', padding: '7px 10px', fontSize: 13, cursor: 'pointer' }}>
                {r.name}{r.region ? `, ${r.region}` : ''} <span style={{ color: '#8aa0af', fontSize: 11.5 }}>({typeLabel[r.type] || r.type})</span>
              </button>
            )) : <div style={{ padding: '8px 10px', fontSize: 12.5, color: '#8aa0af' }}>No matches.</div>}
          </div>
        ) : null}
        <div style={{ marginTop: 8 }}>
          <button style={{ ...miniBtn, borderColor: 'var(--orange)', color: 'var(--orange)' }} onClick={applyGroundPreset} disabled={presetBusy}>
            {presetBusy ? 'Adding zone…' : '+ Next-Day Ground Zone (DE MD NJ VA DC + Philly + NYC)'}
          </button>
        </div>
      </Field>

      {!geoChips.length ? (
        <Field label="Countries (fallback when no locations picked)">
          <input type="text" value={countries} onChange={(e) => setCountries(e.target.value)} style={inputStyle} />
        </Field>
      ) : null}

      <div style={{ display: 'flex', gap: 10 }}>
        <Field label="Age min" style={{ flex: 1 }}>
          <input type="number" min="13" max="65" value={ageMin} onChange={(e) => setAgeMin(parseInt(e.target.value, 10))} style={inputStyle} />
        </Field>
        <Field label="Age max" style={{ flex: 1 }}>
          <input type="number" min="13" max="65" value={ageMax} onChange={(e) => setAgeMax(parseInt(e.target.value, 10))} style={inputStyle} />
        </Field>
      </div>

      <Field label="Gender">
        <div style={{ display: 'flex', gap: 6 }}>
          {[
            { k: 'all',   label: 'All' },
            { k: 'men',   label: 'Men' },
            { k: 'women', label: 'Women' },
          ].map((g) => (
            <button key={g.k} type="button" onClick={() => setGender(g.k)}
              style={{
                padding: '5px 14px', fontSize: 12, fontWeight: 600,
                background: gender === g.k ? 'var(--navy)' : '#fff',
                color: gender === g.k ? '#fff' : 'var(--navy)',
                border: `1px solid ${gender === g.k ? 'var(--navy)' : 'var(--gray-200)'}`,
                borderRadius: 999, cursor: 'pointer',
              }}>
              {g.label}
            </button>
          ))}
        </div>
      </Field>

      {audPicker('Target audiences (optional — lookalikes, customer lists)', incAud, setIncAud, excAud)}
      {audPicker('Exclude audiences (keep your own customers out of prospecting)', excAud, setExcAud, incAud)}

      {audErr ? <div style={{ margin: '6px 0', padding: '8px 10px', background: '#fdf1ee', border: '1px solid #f2c7b8', borderRadius: 6, fontSize: 12.5, color: '#b3401e' }}>{audErr}</div> : null}

      <button style={{ ...miniBtn, width: '100%', marginTop: 4 }} onClick={() => setMgrOpen(!mgrOpen)}>
        {mgrOpen ? 'Hide audience manager' : 'Audience manager — create lists + lookalikes'}
      </button>

      {mgrOpen ? (
        <div style={{ marginTop: 10, border: '1px solid #dfe8ee', borderRadius: 8, padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Create from CPx data</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button style={miniBtn} disabled={!!creating || (job && job.status === 'running')} onClick={() => createFromNeon('all_customers')}>
              {creating === 'all_customers' ? 'Creating…' : 'All customers (~165k emails)'}
            </button>
            <button style={miniBtn} disabled={!!creating || (job && job.status === 'running')} onClick={() => createFromNeon('active_vips')}>
              {creating === 'active_vips' ? 'Creating…' : 'Active VIP members (~6k emails)'}
            </button>
            <button style={miniBtn} disabled={!!creating || (job && job.status === 'running')} onClick={() => createFromNeon('lapsed_vips_unreachable')}>
              {creating === 'lapsed_vips_unreachable' ? 'Creating…' : 'Lapsed VIPs we cannot email (~7.5k)'}
            </button>
          </div>

          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: '.06em', marginTop: 4 }}>Create from Crab &amp; Cruise data</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button style={miniBtn} disabled={!!creating || (job && job.status === 'running')} onClick={() => createFromNeon('cnc_dotdigital_all')}>
              {creating === 'cnc_dotdigital_all' ? 'Creating…' : 'C&C Dotdigital list (~55k)'}
            </button>
            <button style={miniBtn} disabled={!!creating || (job && job.status === 'running')} onClick={() => createFromNeon('cnc_all_customers')}>
              {creating === 'cnc_all_customers' ? 'Creating…' : 'C&C customers, all time (~8.8k)'}
            </button>
            <button style={miniBtn} disabled={!!creating || (job && job.status === 'running')} onClick={() => createFromNeon('cnc_upcoming_tickets')}>
              {creating === 'cnc_upcoming_tickets' ? 'Creating…' : 'C&C upcoming ticket holders (~220) — exclude'}
            </button>
          </div>
          <div style={{ fontSize: 11.5, color: '#8aa0af' }}>
            Built on the <b>main ad account</b> — the one you actually advertise from — so they are ready to pick in an ad set straight away. A Crab &amp; Cruise ad is branded by the Page it runs as, not by the ad account. Ticket holders for events that have not happened yet refresh daily in both directions, so people drop out the morning after their event.
          </div>
          {job ? (
            <div style={{ fontSize: 12.5, color: job.status === 'failed' ? '#b3401e' : 'var(--navy)' }}>
              {job.status === 'done'
                ? <>✓ <b>{job.name}</b> uploaded ({Number(job.total).toLocaleString()} emails). Meta takes a few minutes to match them.</>
                : job.status === 'failed'
                  ? <>✗ Upload failed: {job.error}</>
                  : <>Uploading <b>{job.name}</b>… {Number(job.uploaded || 0).toLocaleString()} / {Number(job.total).toLocaleString()}</>}
            </div>
          ) : null}

          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: '.06em', marginTop: 4 }}>Create a lookalike</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <select value={lalOrigin} onChange={(e) => setLalOrigin(e.target.value)} style={{ ...inputStyle, flex: 1 }}>
              <option value="">Seed audience…</option>
              {(audiences || []).filter((a) => a.subtype !== 'LOOKALIKE').map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
            <select value={lalRatio} onChange={(e) => setLalRatio(e.target.value)} style={{ ...inputStyle, width: 90 }}>
              <option value="0.01">1%</option>
              <option value="0.02">2%</option>
              <option value="0.03">3%</option>
              <option value="0.05">5%</option>
              <option value="0.10">10%</option>
            </select>
            <button style={miniBtn} disabled={!!creating || !lalOrigin} onClick={createLookalike}>
              {creating === 'lookalike' ? 'Creating…' : 'Create'}
            </button>
          </div>
          <div style={{ fontSize: 11.5, color: '#8aa0af' }}>1% = the ~1.6M US people most similar to the seed list. Lookalikes take a few hours to populate; you can target them right away and delivery starts once ready.</div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Account audiences</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <select value={audAcct} onChange={(e) => setAudAcct(e.target.value)}
                style={{ ...inputStyle, width: 'auto', padding: '4px 8px', fontSize: 12 }}>
                <option value="act_47263248">Main ad account</option>
                <option value="act_1297007027607116">Crab Place (legacy)</option>
                <option value="act_1782415362211661">Crab &amp; Cruise (unused)</option>
              </select>
              <button style={{ ...xBtn, color: 'var(--orange)', fontSize: 12 }} onClick={() => loadAudiences(audAcct)} disabled={audBusy}>{audBusy ? 'refreshing…' : 'refresh'}</button>
            </div>
          </div>
          <div style={{ maxHeight: 170, overflowY: 'auto', border: '1px solid #f0f4f7', borderRadius: 6 }}>
            {(audiences || []).map((a) => (
              <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '6px 10px', borderBottom: '1px solid #f5f8fa', fontSize: 12.5 }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name} <span style={{ color: '#8aa0af' }}>{a.subtype === 'LOOKALIKE' ? '· lookalike' : ''}</span></span>
                <span style={{ color: '#8aa0af', flexShrink: 0 }}>{a.sizeLower ? `~${Number(a.sizeLower).toLocaleString()}` : (a.deliveryStatus || '')}</span>
              </div>
            ))}
            {audiences && !audiences.length ? <div style={{ padding: '8px 10px', fontSize: 12.5, color: '#8aa0af' }}>No audiences on the ad account yet — create one above.</div> : null}
          </div>
        </div>
      ) : null}
    </Card>
  );
}

function Card({ title, children }) {
  return (
    <div style={{ background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 8, padding: 18 }}>
      <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--gray-500)', fontWeight: 700, marginBottom: 12 }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{children}</div>
    </div>
  );
}

function Field({ label, children, style = {} }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, ...style }}>
      <label style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--gray-500)', fontWeight: 600 }}>{label}</label>
      {children}
    </div>
  );
}

const inputStyle = {
  padding: '8px 10px', fontSize: 13, border: '1px solid var(--gray-200)', borderRadius: 4,
  background: '#fff', fontFamily: 'inherit', color: '#1d1d1f', width: '100%', boxSizing: 'border-box',
};

const primaryBtn = {
  background: 'var(--orange)', color: '#fff', border: 'none', borderRadius: 6,
  fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
};

const secondaryBtn = {
  background: '#fff', color: 'var(--gray-500)', border: '1px solid var(--gray-200)',
  borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer',
};

function placementBtn(active) {
  return {
    padding: '5px 10px', fontSize: 11, fontWeight: 600,
    background: active ? 'var(--navy)' : '#fff',
    color: active ? '#fff' : '#424245',
    border: `1px solid ${active ? 'var(--navy)' : 'var(--gray-200)'}`,
    borderRadius: 4, cursor: 'pointer',
  };
}

// ── Past campaigns panel + preview modal ─────────────────────────────────────
// A rolling log of paid pushes from this builder, and a screenshot-ready
// preview of any one of them. Storage lives in `meta_paid_pushes` (created +
// written by meta-campaign-push.js); read via meta-organic's `paid-list` /
// `paid-get`.
//
// ⚠️ The preview does NOT try to look pixel-perfect like Facebook — it shows the
// same fields Meta shows on an ad, in the same order, so somebody can screenshot
// it and send it to the team with the copy, image, CTA and destination all
// visible in one frame. Trying to match Facebook exactly is a rabbit hole and
// the preview stops being screenshottable the moment Meta redesigns.

function fmtDollars(cents) {
  if (cents == null) return '';
  const d = cents / 100;
  return d >= 1 ? `$${d.toFixed(0)}` : `$${d.toFixed(2)}`;
}
function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }); }
  catch { return iso; }
}
function domainOf(url) {
  try { return new URL(url).hostname.replace(/^www\./i, '').toUpperCase(); } catch { return ''; }
}
function ctaLabel(t) {
  if (!t) return '';
  return t.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}
function acctLabel(id) {
  if (id === 'act_47263248') return 'Crab Place';
  if (id === 'act_1782415362211661') return 'Crab & Cruise';
  return id || '';
}

// ── Draft campaigns panel ────────────────────────────────────────────────────
// Shared pre-launch work. The top bar is the save control (name + Save draft,
// or "editing X" when one is open); the list below is everyone's open drafts.
function relTime(iso) {
  if (!iso) return '';
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  if (m < 60 * 24) return `${Math.round(m / 60)} hr ago`;
  return fmtDate(iso);
}

function DraftsPanel({ drafts, loading, error, current, name, setName, saving, msg, openingId, onRefresh, onSave, onSaveAsNew, onOverwrite, onReloadTheirs, onOpen, onClose, onDelete }) {
  const [open, setOpen] = useState(true);
  const list = drafts || [];
  const stale = current && list.find((d) => d.id === current.id && d.version > current.version);
  const smallBtn = { background: '#fff', color: 'var(--navy)', border: '1px solid var(--gray-200)', borderRadius: 6, padding: '5px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: 'inherit' };
  return (
    <div style={{ background: '#fff', border: `1px solid ${current ? 'var(--orange)' : 'var(--gray-200)'}`, borderRadius: 8, marginBottom: 20, overflow: 'hidden' }}>
      <button onClick={() => setOpen(!open)}
        style={{ width: '100%', background: 'none', border: 'none', textAlign: 'left', padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, fontFamily: 'inherit' }}>
        <span style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 14 }}>Draft campaigns</span>
        {list.length ? <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', background: 'var(--orange)', borderRadius: 10, padding: '1px 7px' }}>{list.length}</span> : null}
        <span style={{ fontSize: 11, color: 'var(--gray-500)', flex: 1 }}>
          Work in progress the whole team can open, edit and save back before it goes to Meta.
        </span>
        <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>{open ? '▲' : '▼'}</span>
      </button>

      {/* Save bar — always visible so saving never requires expanding the list. */}
      <div style={{ borderTop: '1px solid var(--gray-100)', padding: '10px 16px', background: current ? '#fff7f2' : '#fafafa', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {current ? (
          <span style={{ fontSize: 12, color: 'var(--navy)' }}>
            <strong>Editing draft</strong> · v{current.version} · saved {relTime(current.updated_at)}{current.updated_by_name ? ` by ${current.updated_by_name}` : ''}
          </span>
        ) : (
          <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>Not saved yet.</span>
        )}
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Draft name (defaults to campaign name)"
          style={{ flex: '1 1 220px', minWidth: 180, padding: '6px 10px', fontSize: 12, border: '1px solid var(--gray-200)', borderRadius: 6, fontFamily: 'inherit' }} />
        <button onClick={onSave} disabled={saving}
          style={{ background: 'var(--orange)', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 14px', fontSize: 12, fontWeight: 700, cursor: saving ? 'wait' : 'pointer', fontFamily: 'inherit' }}>
          {saving ? 'Saving…' : current ? 'Save draft' : 'Save as draft'}
        </button>
        {current ? <button onClick={onSaveAsNew} disabled={saving} style={smallBtn} title="Keep the original draft and save this as a separate copy">Save as copy</button> : null}
        {current ? <button onClick={onClose} style={smallBtn}>Close draft</button> : null}
      </div>

      {stale && (!msg || msg.kind !== 'conflict') ? (
        <div style={{ margin: '10px 16px 0', padding: '8px 10px', background: '#fff8e6', border: '1px solid #ffd88a', borderRadius: 6, fontSize: 12, color: '#8a5a00', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <AlertCircle size={13} /> {stale.updated_by_name || 'Someone'} saved a newer version (v{stale.version}) {relTime(stale.updated_at)}.
          <button onClick={onReloadTheirs} style={smallBtn}>Load their version</button>
        </div>
      ) : null}

      {msg ? (
        msg.kind === 'conflict' ? (
          <div style={{ margin: '10px 16px 0', padding: '10px 12px', background: '#fff8e6', border: '1px solid #ffd88a', borderRadius: 6, fontSize: 12, color: '#8a5a00' }}>
            <div style={{ marginBottom: 8 }}><AlertCircle size={13} style={{ verticalAlign: 'middle', marginRight: 6 }} />{msg.text}{msg.current?.updated_at ? ` (${relTime(msg.current.updated_at)})` : ''} Your changes were not saved.</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {msg.conflict === 'version' ? <button onClick={onReloadTheirs} style={smallBtn}>Load their version (drops mine)</button> : null}
              <button onClick={onSaveAsNew} style={smallBtn}>Save mine as a new draft</button>
              {msg.conflict === 'version' ? <button onClick={onOverwrite} style={{ ...smallBtn, color: '#c33', borderColor: '#ffd6d6' }}>Overwrite with mine</button> : null}
            </div>
          </div>
        ) : (
          <div style={{ margin: '10px 16px 0', padding: '8px 10px', borderRadius: 6, fontSize: 12,
            background: msg.kind === 'ok' ? '#f0f9f4' : '#fff5f5', border: `1px solid ${msg.kind === 'ok' ? '#b7e4c7' : '#ffd6d6'}`, color: msg.kind === 'ok' ? '#1a7f37' : '#c33' }}>
            {msg.kind === 'ok' ? <CheckCircle2 size={12} style={{ verticalAlign: 'middle', marginRight: 6 }} /> : <AlertCircle size={12} style={{ verticalAlign: 'middle', marginRight: 6 }} />}{msg.text}
          </div>
        )
      ) : null}

      {open ? (
        <div style={{ padding: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <button onClick={onRefresh} disabled={loading} style={{ ...smallBtn, cursor: loading ? 'wait' : 'pointer' }}>
              {loading ? <><Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} /> Loading…</> : <><RefreshCw size={11} /> Refresh</>}
            </button>
            {list.length ? <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>{list.length} open draft{list.length === 1 ? '' : 's'}</span> : null}
          </div>
          {error ? (
            <div style={{ padding: 10, background: '#fff5f5', border: '1px solid #ffd6d6', borderRadius: 6, fontSize: 12, color: '#c33' }}>
              <AlertCircle size={12} style={{ verticalAlign: 'middle', marginRight: 6 }} />{error}
            </div>
          ) : null}
          {!error && drafts && !list.length ? (
            <div style={{ padding: 14, fontSize: 12, color: 'var(--gray-500)', background: '#fafafa', border: '1px dashed var(--gray-200)', borderRadius: 6, textAlign: 'center' }}>
              No drafts yet. Build a campaign above, then click <strong>Save as draft</strong> so the team can work on it.
            </div>
          ) : null}
          {list.length ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {list.map((d) => {
                const s = d.summary || {};
                const isCurrent = current?.id === d.id;
                return (
                  <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: `1px solid ${isCurrent ? 'var(--orange)' : 'var(--gray-200)'}`, borderRadius: 6, background: isCurrent ? '#fff7f2' : '#fff' }}>
                    <div style={{ width: 40, height: 40, borderRadius: 4, background: '#f0f1f3', flexShrink: 0, overflow: 'hidden' }}>
                      {s.thumbKey ? <img src={`/.netlify/functions/r2-image?key=${encodeURIComponent(s.thumbKey)}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : null}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {d.name}
                        {isCurrent ? <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#fff', background: 'var(--orange)', padding: '1px 6px', borderRadius: 3, verticalAlign: 'middle' }}>OPEN</span> : null}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--gray-500)', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <span>Saved {relTime(d.updated_at)}{d.updated_by_name ? ` by ${d.updated_by_name}` : ''}</span>
                        <span>·</span><span>v{d.version}</span>
                        {s.adCount ? <><span>·</span><span>{s.adCount} {s.format === 'carousel' ? 'carousel' : 'ad'}{s.adCount === 1 ? '' : 's'}</span></> : null}
                        {s.dailyBudgetUsd ? <><span>·</span><span>${Number(s.dailyBudgetUsd).toFixed(0)}/day</span></> : null}
                        {s.source === 'proposal' ? <><span>·</span><span>Claude proposal</span></> : null}
                        {d.created_by_name && d.created_by_name !== d.updated_by_name ? <><span>·</span><span>started by {d.created_by_name}</span></> : null}
                      </div>
                    </div>
                    <button onClick={() => onOpen(d.id)} disabled={openingId === d.id}
                      style={{ background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 4, padding: '6px 12px', fontSize: 11, fontWeight: 600, cursor: openingId === d.id ? 'wait' : 'pointer' }}>
                      {openingId === d.id ? 'Opening…' : isCurrent ? 'Reload' : 'Open'}
                    </button>
                    <button onClick={() => onDelete(d)} title="Delete draft"
                      style={{ background: 'none', border: 'none', color: 'var(--gray-500)', fontSize: 11, cursor: 'pointer', padding: '6px 4px' }}>
                      Delete
                    </button>
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function PastCampaignsPanel({ open, onToggle, list, loading, error, metaWarning, openingId, onRefresh, onOpen }) {
  return (
    <div style={{ background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 8, marginBottom: 20, overflow: 'hidden' }}>
      <button onClick={onToggle}
        style={{ width: '100%', background: 'none', border: 'none', textAlign: 'left', padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, fontFamily: 'inherit' }}>
        <span style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 14 }}>Past campaigns</span>
        <span style={{ fontSize: 11, color: 'var(--gray-500)', flex: 1 }}>
          Every campaign in your Meta ad account, newest first. Open one to preview and screenshot it.
        </span>
        <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open ? (
        <div style={{ borderTop: '1px solid var(--gray-100)', padding: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <button onClick={onRefresh} disabled={loading}
              style={{ background: '#fff', color: 'var(--navy)', border: '1px solid var(--gray-200)', borderRadius: 6, padding: '5px 12px', fontSize: 11, fontWeight: 600, cursor: loading ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {loading ? <><Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} /> Loading…</> : <><RefreshCw size={11} /> Refresh</>}
            </button>
            {list && list.length ? <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>{list.length} campaign{list.length === 1 ? '' : 's'}</span> : null}
          </div>
          {error ? (
            <div style={{ padding: 10, background: '#fff5f5', border: '1px solid #ffd6d6', borderRadius: 6, fontSize: 12, color: '#c33' }}>
              <AlertCircle size={12} style={{ verticalAlign: 'middle', marginRight: 6 }} />{error}
            </div>
          ) : null}
          {!error && metaWarning ? (
            <div style={{ padding: 10, marginBottom: 8, background: '#fff8e6', border: '1px solid #ffd88a', borderRadius: 6, fontSize: 12, color: '#8a5a00' }}>
              <AlertCircle size={12} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              Could not reach Meta ({metaWarning}). Showing what CPx has recorded.
            </div>
          ) : null}
          {!error && list && !list.length ? (
            <div style={{ padding: 14, fontSize: 12, color: 'var(--gray-500)', background: '#fafafa', border: '1px dashed var(--gray-200)', borderRadius: 6, textAlign: 'center' }}>
              No campaigns in this ad account yet.
            </div>
          ) : null}
          {list && list.length ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {list.map((p) => (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: '1px solid var(--gray-200)', borderRadius: 6, background: '#fff' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.campaign_name || '(no name)'}
                      {p.effective_status && p.effective_status !== 'ACTIVE' ? (
                        <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#65676b', background: '#e4e6eb', padding: '1px 6px', borderRadius: 3, verticalAlign: 'middle' }}>{p.effective_status}</span>
                      ) : null}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--gray-500)', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <span>{fmtDate(p.created_at)}</span>
                      {p.ad_count != null ? <><span>·</span><span>{p.ad_count} ad{p.ad_count === 1 ? '' : 's'}</span></> : null}
                      {p.daily_budget_cents ? <><span>·</span><span>{fmtDollars(p.daily_budget_cents)}/day</span></> : null}
                      {p.objective ? <><span>·</span><span>{p.objective.replace(/^OUTCOME_/, '')}</span></> : null}
                      <span>·</span>
                      <span>{acctLabel(p.ad_account_id)}</span>
                      {p.created_by ? <><span>·</span><span>via CPx by {p.created_by}</span></> : null}
                    </div>
                  </div>
                  <button onClick={() => onOpen(p.id)} disabled={openingId === p.id}
                    style={{ background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 4, padding: '6px 12px', fontSize: 11, fontWeight: 600, cursor: openingId === p.id ? 'wait' : 'pointer' }}>
                    {openingId === p.id ? <><Loader2 size={10} style={{ animation: 'spin 1s linear infinite', verticalAlign: 'middle', marginRight: 4 }} /> Opening…</> : 'Preview'}
                  </button>
                  {p.ads_manager_url ? (
                    <a href={p.ads_manager_url} target="_blank" rel="noreferrer"
                      style={{ fontSize: 11, color: 'var(--gray-500)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 3 }}
                      title="Open the campaign in Meta Ads Manager">
                      Ads Manager <ExternalLink size={10} />
                    </a>
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}
          {!list && !loading ? (
            <div style={{ padding: 14, fontSize: 12, color: 'var(--gray-500)', background: '#fafafa', border: '1px dashed var(--gray-200)', borderRadius: 6, textAlign: 'center' }}>
              Click Refresh to load past campaigns.
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}


// A single Facebook-styled ad card. Renders every field Meta shows on the real
// ad (page identity, primary text, image or carousel, headline, description,
// destination domain, CTA), so a screenshot of this is a fair read of what the
// team will see when the campaign goes live.
function PreviewAdCard({ ad }) {
  const isCarousel = ad.format === 'carousel' && Array.isArray(ad.cards) && ad.cards.length;
  const primaryDomain = domainOf(ad.landingUrl);
  return (
    <div style={{ background: '#fff', border: '1px solid #ced0d4', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,0.08)' }}>
      <div style={{ padding: '12px 14px', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--navy)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 15, flexShrink: 0 }}>CP</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, color: '#050505', fontSize: 14, lineHeight: 1.2 }}>The Crab Place</div>
          <div style={{ fontSize: 12, color: '#65676b' }}>Sponsored · <span title="Ad name (internal)">{ad.name}</span></div>
        </div>
      </div>
      {ad.primaryText ? (
        <div style={{ padding: '0 14px 12px', fontSize: 14, color: '#050505', whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>{ad.primaryText}</div>
      ) : null}
      {isCarousel ? (
        <div style={{ display: 'flex', gap: 6, padding: '0 14px 12px', overflowX: 'auto' }}>
          {ad.cards.map((c, i) => (
            <div key={i} style={{ flex: '0 0 220px', border: '1px solid #dadde1', borderRadius: 6, overflow: 'hidden', background: '#fff' }}>
              <div style={{ aspectRatio: '1 / 1', background: '#000', position: 'relative' }}>
                {c.imageUrl ? <img src={c.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : null}
                <div style={{ position: 'absolute', top: 6, left: 6, background: 'rgba(0,0,0,0.7)', color: '#fff', fontSize: 10, padding: '2px 6px', borderRadius: 3, fontWeight: 700 }}>{i + 1} / {ad.cards.length}</div>
              </div>
              <div style={{ padding: '8px 10px', background: '#f0f2f5' }}>
                <div style={{ fontSize: 11, color: '#65676b', textTransform: 'uppercase', letterSpacing: 0.3 }}>{domainOf(c.landingUrl) || primaryDomain}</div>
                {c.headline ? <div style={{ fontSize: 13, fontWeight: 700, color: '#050505', lineHeight: 1.25, marginTop: 2 }}>{c.headline}</div> : null}
                {c.description ? <div style={{ fontSize: 12, color: '#65676b', marginTop: 2 }}>{c.description}</div> : null}
                {ad.ctaType ? (
                  <div style={{ marginTop: 8, background: '#e4e6eb', color: '#050505', textAlign: 'center', padding: '6px 8px', borderRadius: 6, fontSize: 12, fontWeight: 700 }}>{ctaLabel(ad.ctaType)}</div>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <>
          <div style={{ background: '#000' }}>
            {ad.imageUrl ? (
              <img src={ad.imageUrl} alt="" style={{ display: 'block', width: '100%', maxHeight: 520, objectFit: 'contain', background: '#000' }} />
            ) : (
              <div style={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888', fontSize: 12 }}>No image on file for this ad</div>
            )}
          </div>
          <div style={{ padding: '12px 14px', background: '#f0f2f5', display: 'flex', gap: 10, alignItems: 'center' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              {primaryDomain ? <div style={{ fontSize: 11, color: '#65676b', textTransform: 'uppercase', letterSpacing: 0.3 }}>{primaryDomain}</div> : null}
              {ad.headline ? <div style={{ fontSize: 15, fontWeight: 700, color: '#050505', lineHeight: 1.25, marginTop: 2 }}>{ad.headline}</div> : null}
              {ad.description ? <div style={{ fontSize: 12, color: '#65676b', marginTop: 2 }}>{ad.description}</div> : null}
            </div>
            {ad.ctaType ? (
              <div style={{ background: '#e4e6eb', color: '#050505', padding: '10px 14px', borderRadius: 6, fontSize: 13, fontWeight: 700, flexShrink: 0 }}>{ctaLabel(ad.ctaType)}</div>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}

function CampaignPreviewModal({ data, onClose }) {
  const push = data.push || {};
  const ads = Array.isArray(push.ads) ? push.ads : [];
  const sourceLabel = data.source === 'past'
    ? 'Past campaign'
    : data.source === 'proposal'
      ? 'AI proposal preview (not yet pushed)'
      : 'Current ad preview (not yet pushed)';

  // Esc closes.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9999, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '30px 20px', overflowY: 'auto' }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', width: '100%', maxWidth: 780, borderRadius: 10, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--gray-200)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 700 }}>{sourceLabel}</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{push.campaign_name || '(no name)'}</div>
            <div style={{ fontSize: 11, color: 'var(--gray-500)', display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
              {push.objective ? <span>{push.objective.replace(/^OUTCOME_/, '')}</span> : null}
              {push.daily_budget_cents ? <><span>·</span><span>{fmtDollars(push.daily_budget_cents)}/day</span></> : null}
              <span>·</span>
              <span>{ads.length} ad{ads.length === 1 ? '' : 's'}</span>
              {push.created_at ? <><span>·</span><span>{fmtDate(push.created_at)}</span></> : null}
              {push.created_by ? <><span>·</span><span>{push.created_by}</span></> : null}
            </div>
          </div>
          <button onClick={onClose}
            style={{ background: '#fff', color: 'var(--navy)', border: '1px solid var(--gray-200)', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            Close
          </button>
        </div>
        <div style={{ padding: 18, background: '#f0f2f5', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontSize: 12, color: '#606770', background: '#fff8ef', border: '1px solid #ffd9a8', borderRadius: 6, padding: '8px 12px' }}>
            Screenshot this whole panel to send to the team. Each card shows one ad as it will appear on Facebook and Instagram.
          </div>
          {!ads.length ? (
            <div style={{ padding: 20, background: '#fff', borderRadius: 8, textAlign: 'center', fontSize: 13, color: 'var(--gray-500)' }}>
              This campaign has no ads recorded.
            </div>
          ) : null}
          {ads.map((ad, i) => <PreviewAdCard key={ad.id || i} ad={ad} />)}
        </div>
      </div>
    </div>
  );
}
